import { SearchQuery } from "../code/search-query/search-query";
import { CompanyDomain } from "./auth.default";

export const MAX_PAGE_SIZE = 1000;

export enum NotificationType {
  PushNotification = "PushNotification",
  Email = "Email",
  SMS = "SMS",
  MMS = "MMS",
}

export enum NotificationTargetType {
  User = "User",
  Department = "Department",
  Company = "Company",
}

export enum VariableType {
  System = "System",
  Custom = "Custom",
}

export enum ContentType {
  Text = "Text",
  Rich = "Rich",
  Media = "Media",
}

export enum IncidentStatusType {
  Open = "Open",
  InProgress = "InProgress",
  Resolved = "Resolved",
  Closed = "Closed",
}

export enum IncidentSeverityType {
  Low = "Low",
  Medium = "Medium",
  High = "High",
  Critical = "Critical",
}

export enum PermissionActionType {
  None = "None",
  View = "View",
  Create = "Create",
  Edit = "Edit",
  Delete = "Delete",
}

export enum KpiType {
  Basic = "Basic",
  Matrix = "Matrix",
}

export enum KpiScoringMethodType {
  Sum = "Sum",
  Average = "Average",
  Count = "Count",
}

// Mirrors backend SystemEventType, but intentionally OMITS AccountDeactivated — that value has no
// runtime producer (nothing raises it), so binding a trigger to it would be dead config. The backend
// enum keeps it (DB ordinal stability); it's just not offered for trigger creation.
export enum EventTriggerType {
  UserRegistered = "User Registered",
  UserLoggedIn = "User LoggedIn",
  PasswordResetRequested = "Password Reset Requested",
  ConfirmEmailRequested = "Confirm Email Requested",
  ConfirmPhoneNumberRequested = "Confirm Phone Number Requested",
  ConfirmCompanyPhoneNumberRequested = "Confirm Company Phone Number Requested",
  AdminRegistered = "Admin Registered",
  EmployeeRegistered = "Employee Registered",
  ManagerRegistered = "Manager Registered",
  ConsultantRegistered = "Consultant Registered",
  CollaboratorRegistered = "Collaborator Registered",
  IncidentCreated = "Incident Created",
  IncidentAssigned = "Incident Assigned",
  IncidentStatusChanged = "Incident Status Changed",
}

export interface PaginationInput {
  readonly searchQuery: SearchQuery;
  readonly pageSize: number;
  readonly pageNumber: number;
  readonly sortBy?: string | null;
  readonly sortDescending?: boolean | null;
}

export interface Pagination<T> {
  readonly data: T[];
  readonly pageNumber: number;
  readonly pageSize: number;
  readonly totalRecords: number;
}

// Users

export interface PlatformUser {
  readonly uid: string;
  readonly userName: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly middleName: string;
  readonly dateOfBirth: string;
  readonly email: string;
  readonly phoneNumber: string;
  readonly userAvatar: string;
  readonly groupUid: string;
  readonly emailConfirmed: boolean;
  readonly phoneNumberConfirmed?: boolean;
  readonly companyPhoneNumber?: string;
  readonly companyPhoneNumberConfirmed?: boolean;
  readonly companyUid?: string;
  readonly companyName?: string;
  readonly departmentUid?: string;
  readonly departmentName?: string;
  // The backend DTO and the wire have carried these all along; only this interface omitted them, which is
  // what made "no asset column on the users grid" look like a backend gap when it never was one.
  readonly assetUid?: string;
  readonly assetName?: string;
  readonly roleUid?: string;
  readonly roleName?: string;
  readonly tenantUid?: string;
  readonly tenantName?: string;
  readonly loyaltyPoints?: number;
  readonly lockoutEnabled?: boolean;
  readonly lockoutEnd?: string;
  readonly lastLoginAt?: string;
  readonly joinedDate?: string;
  readonly createdOn?: string;
  readonly aboutMe?: string;
  readonly countryUid?: string;
  readonly countryName?: string;
  readonly testLanguageUid?: string;
  readonly testLanguageName?: string;
}

export interface PlatformUserData extends Pagination<PlatformUser> {}

export interface UserAccount {
  readonly uid: string;
  readonly userName: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly middleName: string;
  readonly dateOfBirth: string;
  readonly email: string;
  readonly phoneNumber: string;
  readonly phoneNumberConfirmed: boolean;
  readonly userAvatar: string;
  readonly groupUid: string;
  readonly emailConfirmed: boolean;
  readonly companyPhoneNumber: string;
  readonly companyPhoneNumberConfirmed: boolean;
  readonly companyUid?: string;
  readonly companyName?: string;
  readonly departmentUid?: string;
  readonly departmentName?: string;
  readonly assetUid?: string;
  readonly assetName?: string;
  readonly roleUid?: string;
  readonly roleName?: string;
  readonly lockoutEnabled: boolean;
  readonly lockoutEnd: string;
  readonly lastLoginAt: string;
  readonly platformRoleName: string;
  readonly loyaltyPoints: number;
  readonly emailConfirmationRequestedAt?: string;
  readonly phoneConfirmationRequestedAt?: string;
  // When an administrator last SENT a password reset. Not whether it was used — consuming the token
  // changes the password rather than setting a flag, so that is not knowable from here.
  readonly passwordResetRequestedAt?: string;
  readonly companyPhoneConfirmationRequestedAt?: string;
  readonly aboutMe?: string;
  readonly joinedDate?: string;
  readonly createdOn?: string;
}

export interface ManagerUser {
  readonly uid: string;
  readonly userName: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly middleName: string;
  readonly dateOfBirth: string;
  readonly email: string;
  readonly emailConfirmed: boolean;
  readonly emailConfirmationRequestedAt?: string;
  readonly phoneNumber: string;
  readonly phoneNumberConfirmed?: boolean;
  readonly phoneConfirmationRequestedAt?: string;
  // When an administrator last SENT a password reset. Not whether it was used — consuming the token
  // changes the password rather than setting a flag, so that is not knowable from here.
  readonly passwordResetRequestedAt?: string;
  readonly aboutMe?: string;
  readonly companyPhoneNumber?: string;
  readonly companyPhoneNumberConfirmed?: boolean;
  readonly companyPhoneConfirmationRequestedAt?: string;
  readonly userAvatar: string;
  readonly groupUid: string;
  readonly joinedDate?: string;
  readonly createdOn?: string;
  readonly roleUid?: string;
  readonly roleName?: string;
  readonly tenantUid?: string;
  readonly tenantName?: string;
  readonly companyUid?: string;
  readonly companyName?: string;
  readonly departmentUid?: string;
  readonly departmentName?: string;
  // Optional for a manager — the registration wizard offers an asset but does not require one — so this
  // column is legitimately empty for most of them. It exists because §4.4 asks to FILTER managers by
  // vessel/site, and a filter whose column cannot be seen is untrustworthy.
  readonly assetUid?: string;
  readonly assetName?: string;
  readonly loyaltyPoints?: number;
  readonly lockoutEnabled?: boolean;
  readonly lockoutEnd?: string;
  readonly lastLoginAt?: string;
  readonly countryUid?: string;
  readonly countryName?: string;
  readonly testLanguageUid?: string;
  readonly testLanguageName?: string;
  readonly rowVersion?: string;
}

export interface ManagerUserData extends Pagination<ManagerUser> {}

export interface EmployeeUser {
  readonly uid: string;
  readonly userName: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly middleName: string;
  readonly dateOfBirth: string;
  readonly email: string;
  readonly emailConfirmed: boolean;
  readonly emailConfirmationRequestedAt?: string;
  readonly phoneNumber: string;
  readonly phoneNumberConfirmed?: boolean;
  readonly phoneConfirmationRequestedAt?: string;
  // When an administrator last SENT a password reset. Not whether it was used — consuming the token
  // changes the password rather than setting a flag, so that is not knowable from here.
  readonly passwordResetRequestedAt?: string;
  readonly aboutMe: string;
  readonly companyPhoneNumber: string;
  readonly companyPhoneNumberConfirmed?: boolean;
  readonly companyPhoneConfirmationRequestedAt?: string;
  readonly userAvatar: string;
  readonly joinedDate?: string;
  readonly roleUid: string;
  readonly roleName: string;
  readonly tenantUid: string;
  readonly tenantName: string;
  readonly companyUid?: string;
  readonly companyName?: string;
  readonly departmentUid: string;
  readonly departmentName: string;
  // The vessel / aircraft / site the person is mapped onto — populated for 89 of the 92 seeded employees,
  // and the field the crew-rotation story turns on.
  readonly assetUid?: string;
  readonly assetName?: string;
  readonly gender: string;
  readonly testLanguageUid?: string;
  readonly testLanguageName?: string;
  readonly countryUid?: string;
  readonly countryName?: string;
  readonly internalId: number;
  readonly externalId: string;
  readonly rowVersion: string;
  readonly loyaltyPoints: number;
  readonly lockoutEnabled?: boolean;
  readonly lockoutEnd?: string;
  readonly lastLoginAt?: string;
}

export interface EmployeeUserData extends Pagination<EmployeeUser> {}

// Company

export interface Company {
  readonly uid: string;
  readonly name: string;
  readonly address?: string;
  // Admin requirement 2026-11-03 — structured address fields.
  readonly street?: string;
  readonly streetNumber?: string;
  readonly city?: string;
  readonly postalCode?: string;
  readonly countryUid?: string;
  readonly countryName?: string;
  readonly contactPerson?: string;
  readonly email?: string;
  readonly email2?: string;
  readonly phoneNumber?: string;
  readonly phoneNumber2?: string;
  readonly rowVersion: string;
  readonly tenantUid: string;
  readonly tenantName: string;
  readonly domainUid: string;
  readonly domainName: string;
  readonly parentCompanyUid?: string;
  readonly parentCompanyName?: string;
  readonly departmentsCount: number;
  readonly subCompaniesCount: number;
  readonly usersCount?: number;
  readonly assetsCount?: number;
  readonly registrationNotes?: string;
  readonly subdomain?: string;
  readonly logoBase64?: string | null;
  readonly primaryColour?: string | null;
  // Lifecycle (admin requirement 2026-11-03).
  readonly isActive?: boolean;
  readonly activatedAt?: string | null;
  readonly deactivatedAt?: string | null;
  // Business lifecycle (admin brief 2026-11-04) — Pending / Verified / Trial / Suspended.
  readonly accountStatus?: AccountStatus;
  readonly createdOn?: string;
}

// Admin brief 2026-11-04 — business lifecycle of a company account. Numeric
// values must match the backend PublicEnums.AccountStatus enum on the wire
// (Pending=0, Verified=1, Trial=2, Suspended=3).
export enum AccountStatus {
  Pending = 0,
  Verified = 1,
  Trial = 2,
  Suspended = 3,
}

// Admin brief 2026-11-04 — affiliate verification workflow. Admin-only state
// machine on Consultants / Collaborators. Matches backend
// PublicEnums.VerificationStatus (Pending=0, Verified=1, Rejected=2).
export enum VerificationStatus {
  Pending = 0,
  Verified = 1,
  Rejected = 2,
}

export interface AffiliateVerification {
  readonly verificationStatus: VerificationStatus;
  readonly verifiedAt?: string | null;
  readonly verifiedByUid?: string | null;
  readonly rejectedAt?: string | null;
  readonly rejectedByUid?: string | null;
  readonly rejectionReason?: string | null;
}

export interface Domain {
  readonly uid: string;
  readonly name: string;
  readonly sysName: string;
  readonly description: string;
  readonly rowVersion: string;
}

export interface Profile {
  readonly companyLicense: "None";
  readonly logoBase64: string;
  readonly companyDomain: CompanyDomain;
}

export interface CompanyData extends Pagination<Company> {}

// Admin brief 2026-11-04 — supporting documents (CV, certificates, insurance,
// etc.) attached to a Consultant / Collaborator. Upload + delete are admin-only
// on the backend; the affiliate cannot modify their own documents.
export interface AffiliateDocument {
  readonly uid: string;
  readonly fileName: string;
  readonly fileExtension: string;
  readonly fileSize: number;
  readonly documentType?: string;
  readonly notes?: string;
  readonly userUid: string;
  readonly userName?: string;
  readonly createdOn?: string;
}

// Admin brief 2026-11-04 — full contacts list per company (sits alongside the
// ContactPerson / Email2 / PhoneNumber2 shortcut on the Company record).
export interface CompanyContact {
  readonly uid: string;
  readonly name: string;
  readonly role?: string;
  readonly email?: string;
  readonly phoneNumber?: string;
  readonly isPrimary: boolean;
  readonly notes?: string;
  readonly companyUid: string;
  readonly rowVersion: string;
  readonly createdOn?: string;
}

export interface CompanyContactData extends Pagination<CompanyContact> {}

export interface CompanyContactInput {
  readonly uid?: string;
  readonly name: string;
  readonly role?: string;
  readonly email?: string;
  readonly phoneNumber?: string;
  readonly isPrimary: boolean;
  readonly notes?: string;
  readonly companyUid: string;
  readonly rowVersion?: string;
}

//Company Roles

export interface CompanyRole {
  readonly id: number;
  readonly uid: string;
  readonly name: string;
  readonly normalizedName: string;
  readonly description: string;
  readonly isManager: boolean;
  readonly tenantUid: string;
  readonly tenantName: string;
  readonly companyUid: string;
  readonly companyName: string;
  readonly companyDomainUid: string;
  readonly companyDomainName: string;
  readonly usersCount: number;
  readonly createdOn: string;
  readonly rowVersion: string;
  readonly parentRoleUid: string;
  readonly parentRoleName: string;
  readonly childRolesCount: number;
}

export interface CompanyRoleInput {
  readonly uid?: string;
  readonly name: string;
  readonly description: string;
  readonly isManager: boolean;
  readonly companyUid?: string;
  readonly rowVersion?: string;
  readonly parentRoleUid?: string;
}

export interface AssignedEmployee {
  readonly uid: string;
  readonly userName: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly middleName: string;
  readonly dateOfBirth: string;
  readonly email: string;
  readonly phoneNumber: string;
  readonly userAvatar: string;
  readonly groupUid: string;
  readonly emailConfirmed: boolean;
  readonly companyProfile: CompanyProfile;
}

export interface CompanyProfile {
  readonly companyLicense: number;
  readonly logoBase64: string;
  readonly companyDomain: CompanyIndustry;
}

export interface CompanyRoleData extends Pagination<CompanyRole> {}

export interface CompanyIndustry {
  readonly uid: string;
  readonly rowVersion: string;
  readonly name: string;
  readonly sysName: string;
  readonly description: string;
}

export interface CompanyIndustryData extends Pagination<CompanyIndustry> {}

export interface CompanyPolicy {
  readonly uid: string;
  readonly rowVersion: string;
  readonly name: string;
  readonly code: string;
  // Scope: a company-specific policy carries the owning company; a platform-wide policy has neither.
  readonly companyUid?: string;
  readonly companyName?: string;
  readonly pdfUrl?: string;
}

export interface CompanyPolicyData extends Pagination<CompanyPolicy> {}

export interface CompanyPolicyInput {
  readonly uid?: string;
  readonly rowVersion?: string;
  readonly name: string;
  readonly code: string;
  readonly pdfFile?: File;
  // Admin-only on create: omit for a platform-wide policy, or a company uid to scope it.
  readonly companyUid?: string;
}

export interface CompanyPolicyInputFile {
  readonly uid?: string;
  readonly pdfFile?: File;
}

export interface CompanyIndustryInput {
  readonly uid?: string;
  readonly rowVersion?: string;
  readonly name: string;
  readonly description: string;
  readonly sysName?: string;
}

export interface CompanyInput {
  readonly uid?: string;
  readonly rowVersion?: string;
  readonly name: string;
  readonly address?: string;
  readonly email?: string;
  readonly phoneNumber?: string;
  readonly domainUid?: string;
  readonly parentCompanyUid?: string;
  readonly registrationNotes?: string;
  // PD-01 §3 branding + subdomain. Subdomain is set-once at creation (backend
  // locks it on update); logo + primaryColour are editable afterwards.
  readonly subdomain?: string;
  readonly logoBase64?: string | null;
  readonly primaryColour?: string | null;
  // Admin brief 2026-11-04 — business lifecycle dropdown.
  readonly accountStatus?: AccountStatus;
}

//Departments

export interface Department {
  readonly id: number;
  readonly uid: string;
  readonly name: string;
  readonly normalizedName: string;
  readonly tenantUid: string;
  readonly companyUid: string;
  readonly companyName: string;
  readonly rowVersion: string;
  readonly usersCount: number;
  readonly tenantName?: string;
  readonly createdOn?: string;
  readonly updatedOn?: string;
  readonly assignedEmployees: AssignedEmployee[];
}

export interface DepartmentInput {
  readonly uid?: string;
  readonly rowVersion?: string;
  readonly name: string;
  readonly companyUid?: string;
}

export interface DepartmentData extends Pagination<Department> {}

// Fiscal Periods

export enum FiscalPeriodType {
  Year = "Year",
  Semester = "Semester",
  Quarter = "Quarter",
}

export interface FiscalPeriod {
  readonly uid: string;
  readonly name: string;
  readonly normalizedName: string;
  readonly periodType: FiscalPeriodType;
  readonly startDate: string; // "YYYY-MM-DD"
  readonly endDate: string; // "YYYY-MM-DD"
  readonly year: number;
  readonly parentFiscalPeriodUid?: string;
  readonly parentName?: string;
  readonly tenantUid?: string;
  readonly companyUid: string;
  readonly companyName: string;
  readonly tenantName?: string;
  readonly rowVersion: string;
  readonly createdOn?: string;
  readonly updatedOn?: string;
}

export interface FiscalPeriodInput {
  readonly uid?: string;
  readonly rowVersion?: string;
  readonly name: string;
  readonly periodType: FiscalPeriodType;
  readonly startDate: string;
  readonly endDate: string;
  readonly year: number;
  readonly parentFiscalPeriodUid?: string;
  readonly companyUid?: string;
}

export interface FiscalPeriodData extends Pagination<FiscalPeriod> {}

// Data Logs

export interface DataLog {
  readonly id: number;
  readonly tableName: string;
  readonly dateTime: string;
  readonly keyValues: string;
  readonly oldValues: string;
  readonly newValues: string;
  readonly sourceUid: string;
  readonly sourceType: string;
}

export interface DataLogData extends Pagination<DataLog> {}

// Action Logs

export interface ActionLog {
  readonly uid: string;
  readonly userUid: string;
  readonly userUserName: string;
  readonly controller: string;
  readonly action: string;
  readonly ipAddress: string;
  readonly userAgent: string;
  readonly actionDescription: string;
  readonly companyUid: string;
  readonly companyName: string;
  readonly tenantUid: string;
  readonly tenantName: string;
  readonly timestamp: string;
  readonly rowVersion: string;
}

export interface ActionLogData extends Pagination<ActionLog> {}

//Rewards

export interface RewardInput {
  readonly uid?: string;
  readonly name: string;
  readonly description: string;
  readonly rowVersion?: string;
  readonly requiredPoints: number;
  readonly isPublished?: boolean;
  readonly isPlatformWide?: boolean;
}

export interface Reward {
  readonly uid: string;
  readonly name: string;
  readonly description: string;
  readonly rowVersion: string;
  readonly createdOn: string;
  readonly companyUid?: string;
  readonly companyName?: string;
  readonly rewardedCompaniesCount: number;
  readonly rewardedDepartmentsCount: number;
  readonly rewardedUsersCount: number;
  readonly requiredPoints: number;
  readonly isPublished: boolean;
  readonly isPlatformWide: boolean;
}

export interface RewardsData extends Pagination<Reward> {}
interface BaseReward {
  readonly uid: string;
  readonly rewardUid: string;
  readonly rewardName: string;
  readonly assignedByUserUid: string;
  readonly assignedByUserUserName: string;
  readonly assignmentDate: Date;
  readonly expirationDate: Date;
  readonly isActive: boolean;
  readonly isDeleted: boolean;
  readonly rowVersion: string;
  readonly createdOn: Date;
  readonly updatedOn: Date;
}

interface BaseRewardInput {
  readonly rowVersion?: string;
  readonly rewardUid?: string;
  readonly assignmentDate: Date;
  readonly expirationDate: Date;
}

export interface CompanyRewardInput extends BaseRewardInput {
  readonly companyUid?: string;
}
export interface CompanyReward extends BaseReward {
  readonly companyUid: string;
  readonly companyName: string;
}

export interface CompanyRewardsData extends Pagination<CompanyReward> {}

export interface DepartmentRewardInput extends BaseRewardInput {
  readonly departmentUid?: string;
}

export interface DepartmentReward extends BaseReward {
  readonly departmentUid: string;
  readonly departmentName: string;
}

export interface DepartmentsRewardsData extends Pagination<DepartmentReward> {}

export interface UserRewardInput extends BaseRewardInput {
  readonly userUid?: string;
}
export interface UserReward extends BaseReward {
  readonly userUid: string;
  readonly userUserName: string;
}

export interface UsersRewardsData extends Pagination<UserReward> {}

// Reward redemptions

export enum RewardRedemptionStatus {
  Pending = "Pending",
  Approved = "Approved",
  Fulfilled = "Fulfilled",
  Rejected = "Rejected",
  Cancelled = "Cancelled",
}

export interface RewardRedemption {
  readonly uid: string;
  readonly userUid: string;
  readonly userUserName: string;
  readonly rewardUid: string;
  readonly rewardName: string;
  readonly departmentUid?: string;
  readonly departmentName?: string;
  readonly pointsSpent: number;
  readonly status: RewardRedemptionStatus;
  readonly requestedOn: string;
  readonly decidedOn?: string;
  readonly decidedByUserUid?: string;
  readonly decidedByUserUserName?: string;
  readonly notes?: string;
  readonly companyUid?: string;
  readonly companyName?: string;
  readonly tenantUid?: string;
  readonly tenantName?: string;
  readonly createdOn: string;
  readonly rowVersion: string;
}

export interface RewardRedemptionData extends Pagination<RewardRedemption> {}

export interface RewardRedemptionRejectInput {
  readonly notes?: string;
}

// Calendar

// Calendar event participant roles / invite responses.
// Backend serializes enums as strings (CustomJsonStringEnumConverter), matching the IncidentStatusType convention.
export enum EventParticipantRole {
  Attendee = "Attendee",
  Host = "Host",
}
export enum InviteResponse {
  Pending = "Pending",
  Accepted = "Accepted",
  Declined = "Declined",
}

export interface EventHost {
  readonly userUid: string;
  readonly userUserName?: string;
}

export interface CalendarEventInput {
  readonly uid?: string;
  readonly title: string;
  readonly description: string;
  readonly startDateTime: Date;
  readonly endDateTime: Date;
  readonly companyUid?: string;
  // Zero, one, or many target departments. All must belong to the event's company. Empty/omitted = company-wide.
  readonly departmentUids?: string[];
  readonly typeUid: string;
  readonly rowVersion?: string;
  // Facilitating hosts (replaces the single assignedToUserUid). The creator is auto-added as a host server-side.
  readonly hostUserUids?: string[];
}

export interface CalendarEventDepartment {
  readonly departmentUid: string;
  readonly departmentName: string;
}

export interface CalendarEvent {
  readonly uid: string;
  readonly title: string;
  readonly description: string;
  readonly startDateTime: string;
  readonly endDateTime: string;
  readonly createdOn: string;
  readonly rowVersion: string;
  readonly companyUid: string;
  readonly companyName: string;
  readonly departments: CalendarEventDepartment[];
  readonly typeUid: string;
  readonly typeName: string;
  readonly invitedUsersCount: number;
  // Provenance: which domain object created this event (e.g. "CounsellingRequest"), if any.
  readonly originType?: string;
  readonly originUid?: string;
  // Hosts (invites with Role=Host) replace the former single assignee.
  readonly hosts: EventHost[];
  readonly hostCount: number;
}

export interface CalendarEventsData extends Pagination<CalendarEvent> {}

export interface CalendarEventType {
  readonly uid: string;
  readonly name: string;
  // System-reserved code for special types (e.g. "CounsellingSession"); null for ordinary types.
  readonly code?: string;
  readonly description: string;
  readonly rowVersion: string;
  readonly createdOn: string;
  readonly totalEventsCount: number;
}

export interface CalendarEventTypesData extends Pagination<CalendarEventType> {}

export interface CalendarInvitation {
  readonly uid: string;
  readonly eventUid: string;
  readonly eventTitle: string;
  readonly eventDescription: string;

  readonly userUid: string;
  readonly userUserName: string;

  readonly eventStartDate: string;
  readonly eventEndDate: string;

  readonly eventTypeUid: string;
  readonly eventTypeName: string;

  readonly eventCompanyUid: string;
  readonly eventCompanyName: string;

  readonly eventDepartments: CalendarEventDepartment[];

  readonly invitedByUserUid?: string;
  readonly invitedByUserUserName?: string;

  // Participant role + response (replace the former single assignee / hasAccepted model).
  readonly role: EventParticipantRole;
  readonly response: InviteResponse;
  readonly respondedAt?: string;

  // Attendance mark, recorded by a training host against the invitee list (null until marked).
  readonly attended?: boolean;
  readonly attendedAt?: string;

  readonly invitedUsersCount: number;
  readonly createdOn: string;
  readonly rowVersion: string;
}

export interface CalendarInvitationsData
  extends Pagination<CalendarInvitation> {}

export interface CalendarInvitationsInput {
  readonly uid: string;
}

interface CalendarInvitationInput {
  readonly rowVersion?: string;
  readonly eventUid?: string;
}

export interface CalendarCompanyInvitationInput
  extends CalendarInvitationInput {
  readonly companyUid?: string;
}

export interface CalendarDepartmentInvitationInput
  extends CalendarInvitationInput {
  readonly departmentUid?: string;
}

export interface CalendarUserInvitationInput extends CalendarInvitationInput {
  readonly userUid?: string;
  // Defaults to Attendee server-side when omitted; pass Host to invite/promote a host.
  readonly role?: EventParticipantRole;
}

//Events Types
export interface EventType {
  readonly uid: string;
  readonly name: string;
  readonly description: string;
  readonly rowVersion: string;
  readonly createdOn: string;
  readonly code: string;
  readonly totalEventsCount: number;
}

export interface EventsTypesData extends Pagination<EventType> {}

export interface EventsTypesInput {
  readonly uid?: string;
  readonly rowVersion?: string;
  readonly name: string;
  readonly description: string;
  readonly code?: string;
}

// Badges

export interface Badge {
  readonly uid: string;
  readonly name: string;
  readonly code: string;
  readonly description: string;
  readonly triggerEvent?: "Login" | "QuestionnaireCompleted" | null;
  readonly threshold?: number;
  readonly pointReward?: number;
  readonly isActive: boolean;
  readonly createdOn: string;
  readonly updatedOn: string;
  readonly badgedCompaniesCount: number;
  readonly badgedDepartmentsCount: number;
  readonly badgedUsersCount: number;
  readonly rowVersion: string;
  readonly isDeleted: boolean;
}

export interface BadgesData extends Pagination<Badge> {}

// Assigned Badges

export interface BaseBadge {
  readonly uid: string;
  readonly badgeUid: string;
  readonly badgeName: string;
  readonly assignedByUserUid: string;
  readonly assignedByUserUserName: string;
  readonly assignmentDate: Date;
  readonly expirationDate: Date;
  readonly isActive: boolean;
  readonly isDeleted: boolean;
  readonly rowVersion: string;
  readonly createdOn: Date;
  readonly updatedOn: Date;
}
// Badge-assignment inputs — mirror the reward-assignment inputs (dates in the POST body).
interface BaseBadgeInput {
  readonly rowVersion?: string;
  readonly badgeUid?: string;
  readonly assignmentDate: Date;
  readonly expirationDate: Date;
}

export interface CompanyBadgeInput extends BaseBadgeInput {
  readonly companyUid?: string;
}

export interface DepartmentBadgeInput extends BaseBadgeInput {
  readonly departmentUid?: string;
}

export interface UserBadgeInput extends BaseBadgeInput {
  readonly userUid?: string;
}

export interface CompanyBadge extends BaseBadge {
  readonly companyUid: string;
  readonly companyName: string;
}

export interface CompanyBadgesData extends Pagination<CompanyBadge> {}

export interface DepartmentBadge extends BaseBadge {
  readonly departmentUid: string;
  readonly departmentName: string;
}

export interface DepartmentsBadgesData extends Pagination<DepartmentBadge> {}

export interface UserBadge extends BaseBadge {
  readonly userUid: string;
  readonly userUserName: string;
}

export interface UsersBadgesData extends Pagination<UserBadge> {}

export interface UserHistory {
  readonly uid: string;
  readonly loginProvider: string;
  readonly timestamp: string;
  readonly ipAddress: string;
  readonly deviceInfo: string;
  readonly userUid: string;
  readonly userUserName: string;
  readonly companyUid: string;
  readonly companyName: string;
  readonly departmentUid: string;
  readonly departmentName: string;
}

export interface UserHistoryData extends Pagination<UserHistory> {}

// Templates

export interface Category {
  readonly uid: string;
  readonly rowVersion: string;
  readonly name: string;
  readonly code: string;
  readonly description: string;
  readonly templatesCount: number;
  readonly contentType: string;
}

export interface CategoriesData extends Pagination<Category> {}

export interface CategoriesInput {
  readonly uid?: string;
  readonly rowVersion?: string;
  readonly name: string;
  readonly description: string;
  readonly code: string;
  readonly contentType?: string;
}

// Languages (PD-01 §20) — platform-level config, used to filter
// assessments and templates by language. The `Language` + `LanguagesData`
// interfaces live further down (shared with the Bundle-language picker).
export interface LanguageInput {
  readonly uid?: string;
  readonly rowVersion?: string;
  readonly name: string;
  readonly code: string;
  readonly alpha3Code?: string;
  readonly isTestLanguage?: boolean;
}

// Incident Follow-Ups (PD-01 §11) — remediation actions tracked per incident.
export enum IncidentFollowUpStatus {
  Open = "Open",
  InProgress = "InProgress",
  Completed = "Completed",
  Cancelled = "Cancelled",
}

export interface IncidentFollowUp {
  readonly uid: string;
  readonly rowVersion: string;
  readonly incidentUid: string;
  readonly incidentTitle?: string;
  readonly action: string;
  readonly ownerUid?: string | null;
  readonly ownerUserName?: string | null;
  readonly dueDate?: string | null;
  readonly status: IncidentFollowUpStatus;
  readonly evidence?: string | null;
  readonly completedAt?: string | null;
  readonly createdOn?: string;
  readonly updatedOn?: string;
}

export interface IncidentFollowUpsData extends Pagination<IncidentFollowUp> {}

export interface IncidentFollowUpInput {
  readonly uid?: string;
  readonly rowVersion?: string;
  readonly incidentUid?: string;
  readonly action?: string;
  readonly ownerUid?: string | null;
  readonly dueDate?: string | null;
  readonly status?: IncidentFollowUpStatus;
  readonly evidence?: string | null;
}

// Policy Acknowledgments (PD-01 §6) — audit records.
export interface CompanyPolicyAcknowledgment {
  readonly uid: string;
  readonly policyUid: string;
  readonly policyName: string;
  readonly userUid: string;
  readonly userUserName?: string;
  readonly userFirstName?: string;
  readonly userLastName?: string;
  readonly companyUid?: string;
  readonly companyName?: string;
  readonly departmentUid?: string;
  readonly departmentName?: string;
  readonly acknowledgedAt: string;
  readonly deviceInfo?: string;
  readonly ipAddress?: string;
  readonly createdOn?: string;
}

export interface CompanyPolicyAcknowledgmentsData extends Pagination<CompanyPolicyAcknowledgment> {}

// Points Rules (PD-01 §8) — platform points accrual rules.
export interface PointsRule {
  readonly uid: string;
  readonly rowVersion: string;
  readonly eventType: string;
  readonly points: number;
  readonly isActive: boolean;
  readonly description?: string;
  readonly createdOn?: string;
  readonly updatedOn?: string;
}

export interface PointsRulesData extends Pagination<PointsRule> {}

export interface PointsRuleInput {
  readonly uid?: string;
  readonly rowVersion?: string;
  readonly eventType?: string;
  readonly points?: number;
  readonly isActive?: boolean;
  readonly description?: string;
}

// Login Logs (PD-01 §21) — authentication-attempt audit log.
export interface LoginLog {
  readonly uid: string;
  readonly loginProvider: string;
  readonly timestamp: string;
  readonly ipAddress?: string;
  readonly deviceInfo?: string;
  readonly userUid?: string;
  readonly userUserName?: string;
  readonly companyUid?: string;
  readonly companyName?: string;
  readonly departmentUid?: string;
  readonly departmentName?: string;
}

export interface LoginLogsData extends Pagination<LoginLog> {}

// Program Categories (PD-01 §20) — classification for programs/trainings
// surfaced in the Wellbeing Corner.
export interface ProgramCategory {
  readonly uid: string;
  readonly rowVersion: string;
  readonly name: string;
  readonly description?: string;
  readonly programsCount?: number;
  readonly createdOn?: string;
  readonly updatedOn?: string;
}

export interface ProgramCategoriesData extends Pagination<ProgramCategory> {}

export interface ProgramCategoryInput {
  readonly uid?: string;
  readonly rowVersion?: string;
  readonly name: string;
  readonly description?: string;
}

export interface Variable {
  readonly uid: string;
  readonly rowVersion: string;
  readonly name: string;
  readonly tag: string;
  readonly description: string;
  readonly contentType: ContentType;
  readonly variableType: VariableType;
  readonly value: string;
}

export interface VariablesData extends Pagination<Variable> {}

export interface VariablesInput {
  readonly uid?: string;
  readonly rowVersion?: string;
  readonly name: string;
  readonly description: string;
  readonly tag: string;
  readonly contentType: string;
  readonly value: string;
}

// Templates

export interface Template {
  readonly uid: string;
  readonly name: string;
  readonly subject: string;
  readonly body: string;
  readonly version: number;
  readonly notificationType: string;
  readonly categoryUid: string;
  readonly categoryName: string;
  readonly createdOn: string;
  readonly updatedOn: string;
  readonly rowVersion: string;
}

export interface TemplatesData extends Pagination<Template> {}

export interface TemplatesInput {
  readonly uid?: string;
  readonly rowVersion?: string;
  readonly name: string;
  readonly subject: string;
  readonly body: string;
  readonly categoryUid: string;
  readonly notificationType: string;
}

// Templates Versions
export interface TemplateVersion {
  readonly uid: string;
  readonly version: number;
  readonly name: string;
  readonly subject: string;
  readonly body: string;
  readonly templateUid: string;
  readonly rowVersion: string;
  readonly createdOn: string;
  readonly createdByUserUserName?: string;
}

export interface TemplateVersionsData extends Pagination<TemplateVersion> {}

export interface TemplatePreviewInput {
  readonly subject?: string;
  readonly body?: string;
  readonly notificationType?: string;
  readonly categoryUid?: string;
}

export interface TemplatePreview {
  readonly subject: string;
  readonly body: string;
}

// Notifications

export enum NotificationDeliveryStatus {
  Pending = "Pending",
  Sent = "Sent",
  Failed = "Failed",
}

export interface MyNotification {
  readonly uid: string;
  readonly title: string;
  readonly message: string;
  readonly sentByUid: string;
  readonly sentByUserName: string;
  readonly sentAt: string;
  readonly deliveredAt: string;
  readonly isRead: boolean;
  readonly readAt: string;
  readonly createdOn: string;
  readonly rowVersion: string;
}

export interface MyNotificationsData extends Pagination<MyNotification> {}

export interface MyNotificationRead {
  readonly uid?: string;
}

export interface Notification {
  readonly uid: string;
  readonly title: string;
  readonly message: string;
  readonly type: NotificationType;
  readonly sentByUid: string;
  readonly sentByUserName: string;
  readonly isSent: boolean;
  readonly sentAt: string;
  readonly scheduledAt: string;
  readonly recipientsCount: number;
  readonly sentCount: number;
  readonly deliveredCount: number;
  readonly readCount: number;
  readonly createdOn: string;
  readonly rowVersion: string;
}

export interface NotificationsData extends Pagination<Notification> {}

export interface NotificationInput {
  readonly uid?: string;
  readonly rowVersion?: string;
  readonly title: string;
  readonly message: string;
  readonly type: string;
  readonly scheduledAt: Date;
  readonly targets: {
    readonly targetType: NotificationTargetType;
    readonly targetUid: string;
  }[];
}

// Notification Recipients

export interface NotificationRecipient {
  readonly uid: string;
  readonly createdOn: string;
  readonly createdBy: string;
  readonly createdByUserUserName: string;
  readonly updatedOn: string;
  readonly updatedBy: string;
  readonly updatedByUserUserName: string;
  readonly isDeleted: string;
  readonly rowVersion: string;
  readonly userUid: string;
  readonly userUserName: string;
  readonly companyUid: string;
  readonly companyName: string;
  readonly departmentUid: string;
  readonly departmentName: string;
  readonly isRead: boolean;
  readonly readAt: string;
  readonly notificationUid: string;
  readonly notificationTitle: string;
  readonly notificationMessage: string;
  readonly isSent: boolean;
  readonly sentAt: string;
  readonly deliveryStatus: NotificationDeliveryStatus;
  readonly failedReason: string;
  readonly deliveredAt: string;
}

export interface NotificationRecipientsData
  extends Pagination<NotificationRecipient> {}

// Triggers

export interface TemplateEvent {
  readonly uid: string;
  readonly templateUid: string;
  readonly templateName: string;
  readonly event: string;
  readonly isActive: boolean;
  readonly createdOn: string;
  readonly updatedOn: string;
  readonly rowVersion: string;
}

export interface TemplateEventsData extends Pagination<TemplateEvent> {}

export interface TemplateEventInput {
  readonly uid?: string;
  readonly rowVersion?: string;
  readonly event: string;
  readonly templateUid?: string;
  readonly isActive: boolean;
}

// Incidents

export interface Incident {
  readonly uid: string;
  readonly title: string;
  readonly createdOn: string;
  readonly description: string;
  readonly reportedDate: string;
  readonly status: IncidentStatusType;
  readonly severity: IncidentSeverityType;
  readonly reportedByUserUid: string;
  readonly reportedByUserUserName: string;
  readonly assignedToUserUid: string;
  readonly assignedToUserUserName: string;
  readonly companyUid: string;
  readonly companyName: string;
  readonly departmentUid: string;
  readonly departmentName: string;
  readonly incidentTypeUid?: string;
  readonly incidentTypeName?: string;
  readonly tenantUid: string;
  readonly tenantName: string;
  readonly commentsCount: number;
  readonly rowVersion: string;
  readonly isAnonymous: boolean;
}

export interface IncidentsData extends Pagination<Incident> {}

export interface IncidentInput {
  readonly uid?: string;
  readonly rowVersion?: string;
  readonly title: string;
  readonly description: string;
  readonly status: IncidentStatusType;
  readonly severity: IncidentSeverityType;
  readonly companyUid: string;
  readonly departmentUid: string;
  readonly assignedToUserUid: string;
  readonly incidentTypeUid?: string;
  readonly isAnonymous: boolean;
}

// The reporter behind an anonymous incident — only returned by the admin-only reveal endpoint.
export interface IncidentReporter {
  readonly reportedByUserUid?: string;
  readonly reportedByUserUserName?: string;
}

// Incident Types (admin reference catalog) + per-company notify rosters.

export interface IncidentType {
  readonly uid: string;
  readonly name: string;
  readonly description: string;
  readonly createdOn: string;
  readonly rowVersion: string;
}

export interface IncidentTypesData extends Pagination<IncidentType> {}

export interface IncidentTypeInput {
  readonly uid?: string;
  readonly rowVersion?: string;
  readonly name: string;
  readonly description: string;
}

export interface AssetType {
  readonly uid: string;
  readonly name: string;
  readonly description: string;
  readonly domainUid?: string;
  readonly domainName?: string;
  readonly subtypesCount: number;
  readonly assetsCount: number;
  readonly createdOn: string;
  readonly rowVersion: string;
}

export interface AssetTypesData extends Pagination<AssetType> {}

export interface AssetTypeInput {
  readonly uid?: string;
  readonly rowVersion?: string;
  readonly name: string;
  readonly description?: string;
  readonly domainUid?: string;
}

export interface AssetSubtype {
  readonly uid: string;
  readonly name: string;
  readonly description: string;
  readonly assetTypeUid: string;
  readonly assetTypeName: string;
  readonly assetsCount: number;
  readonly createdOn: string;
  readonly rowVersion: string;
}

export interface AssetSubtypesData extends Pagination<AssetSubtype> {}

export interface AssetSubtypeInput {
  readonly uid?: string;
  readonly rowVersion?: string;
  readonly name: string;
  readonly description?: string;
  readonly assetTypeUid: string;
}

export interface CompanyAsset {
  readonly uid: string;
  readonly name: string;
  readonly code?: string;
  readonly assetTypeUid: string;
  readonly assetTypeName: string;
  readonly assetSubtypeUid?: string;
  readonly assetSubtypeName?: string;
  readonly companyUid?: string;
  readonly companyName?: string;
  readonly usersCount: number;
  readonly rowVersion: string;
}

export interface CompanyAssetsData extends Pagination<CompanyAsset> {}

export interface CompanyAssetInput {
  readonly uid?: string;
  readonly rowVersion?: string;
  readonly name: string;
  readonly code?: string;
  readonly assetTypeUid: string;
  readonly assetSubtypeUid?: string;
  readonly companyUid?: string;
}

export interface IncidentTypeNotifier {
  readonly uid: string;
  readonly incidentTypeUid: string;
  readonly incidentTypeName: string;
  readonly companyRoleUid: string;
  readonly companyRoleName: string;
  readonly companyUid: string;
  readonly createdOn?: string;
  readonly rowVersion: string;
}

export interface IncidentTypeNotifiersData
  extends Pagination<IncidentTypeNotifier> {}

export interface IncidentTypeNotifierInput {
  readonly uid?: string;
  readonly rowVersion?: string;
  readonly incidentTypeUid: string;
  readonly companyRoleUid: string;
  readonly companyUid?: string;
}

// Incident Comments

export interface IncidentComment {
  readonly uid: string;
  readonly incidentUid: string;
  readonly userUid: string;
  readonly userUserName: string;
  readonly comment: string;
  readonly createdOn: string;
  // Stamped server-side on edit (SaveChangesAuthContextAsync); absent/equal-to-createdOn on a fresh comment.
  readonly updatedOn?: string;
  readonly rowVersion: string;
}

export interface IncidentCommentsData extends Pagination<IncidentComment> {}

export interface IncidentCommentsInput extends PaginationInput {
  readonly incidentUid: string;
}

export interface AddIncidentCommentInput {
  readonly incidentUid: string;
  readonly comment: string;
}

export interface IncidentCommentInput {
  readonly uid?: string;
  readonly rowVersion?: string;
  readonly comment: string;
}

// Labels
export interface Label {
  readonly uid: string;
  readonly createdOn: string;
  readonly createdBy: string;
  readonly createdByUserUserName: string;
  readonly updatedOn: string;
  readonly updatedBy: string;
  readonly updatedByUserUserName: string;
  readonly isDeleted: boolean;
  readonly rowVersion: string;
  readonly name: string;
  readonly normalizedName: string;
  readonly description: string;
}

export interface LabelsData extends Pagination<Label> {}

export interface LabelInput {
  readonly uid?: string;
  readonly rowVersion?: string;
  readonly name: string;
  readonly description: string;
}

// Company Roles Labels

export interface CompanyRoleLabel {
  readonly uid: string;
  readonly createdOn: string;
  readonly createdBy: string;
  readonly createdByUserUserName: string;
  readonly updatedOn: string;
  readonly updatedBy: string;
  readonly updatedByUserUserName: string;
  readonly isDeleted: boolean;
  readonly rowVersion: string;
  readonly companyUid: string;
  readonly companyRoleUid: string;
  readonly companyRoleName: string;
  readonly labelUid: string;
  readonly labelName: string;
  readonly labelDescription: string;
}

export interface CompanyRolesLabelsData extends Pagination<CompanyRoleLabel> {}

export interface CompanyRoleLableInput {
  readonly roleUid: string;
  readonly labelUids: string[];
}

// User Labels
export interface UserLabel {
  readonly uid: string;
  readonly createdOn: string;
  readonly createdBy: string;
  readonly createdByUserUserName: string;
  readonly updatedOn: string;
  readonly updatedBy: string;
  readonly updatedByUserUserName: string;
  readonly isDeleted: boolean;
  readonly rowVersion: string;
  readonly companyUid: string;
  readonly userUid: string;
  readonly userUserName: string;
  readonly labelUid: string;
  readonly labelName: string;
  readonly labelDescription: string;
}

export interface UserLabelsData extends Pagination<UserLabel> {}

export interface UserLableInput {
  readonly userUid: string;
  readonly labelUids: string[];
}

//Languages
export interface Language {
  readonly uid: string;
  readonly name: string;
  readonly code: string;
  readonly alpha3Code: string;
  readonly isTestLanguage: boolean;
  readonly rowVersion: string;
  readonly createdOn: string;
}

export interface LanguagesData extends Pagination<Language> {}

//Countries
export interface Country {
  readonly uid: string;
  readonly name: string;
  readonly code: string;
  readonly alpha3Code: string;
  readonly numericCode: string;
  readonly languageCode: string;
  readonly rowVersion: string;
  readonly createdOn: string;
}

export interface CountriesData extends Pagination<Country> {}

// Collaborators

export interface Collaborator extends Partial<AffiliateVerification> {
  readonly uid: string;
  readonly userName: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly middleName: string;
  readonly dateOfBirth: string;
  readonly email: string;
  readonly emailConfirmed: boolean;
  readonly phoneNumber: string;
  readonly phoneNumberConfirmed: boolean;
  readonly tenantUid: string;
  readonly tenantName: string;
  readonly companyUid: string;
  readonly companyName: string;
  readonly assignedCompaniesCount: number;
  readonly rowVersion: string;
  readonly lockoutEnabled: boolean;
  readonly lockoutEnd: string;
  readonly createdOn?: string;
}

export interface CollaboratorsData extends Pagination<Collaborator> {}

export interface CollaboratorInput {
  readonly identifier: string;
  readonly firstName?: string;
  readonly middleName?: string;
  readonly lastName?: string;
  readonly email?: string;
  readonly phoneNumber?: string;
  readonly companyUid?: string;
}

// Tenants
export interface Tenant {
  readonly uid: string;
  readonly name: string;
  readonly normalizedName: string;
  readonly description: string;
  readonly isDeleted: boolean;
  readonly rowVersion: string;
}

export interface TenantsData extends Pagination<Tenant> {}

// Consultants

export interface Consultant extends Partial<AffiliateVerification> {
  readonly uid: string;
  readonly userName: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly middleName: string;
  readonly dateOfBirth: string;
  readonly email: string;
  readonly emailConfirmed: boolean;
  readonly phoneNumber: string;
  readonly phoneNumberConfirmed: boolean;
  readonly tenantUid: string;
  readonly tenantName: string;
  readonly companyUid: string;
  readonly companyName: string;
  readonly assignedCompaniesCount: number;
  readonly rowVersion: string;
  readonly lockoutEnabled: boolean;
  readonly lockoutEnd: string;
  readonly createdOn?: string;
}

export interface ConsultantsData extends Pagination<Consultant> {}

export interface ConsultantInput {
  readonly identifier: string;
  readonly firstName?: string;
  readonly middleName?: string;
  readonly lastName?: string;
  readonly email?: string;
  readonly phoneNumber?: string;
  readonly companyUid?: string;
}

export interface AdminInput {
  readonly identifier: string;
  readonly firstName?: string;
  readonly middleName?: string;
  readonly lastName?: string;
  readonly email?: string;
  readonly phoneNumber?: string;
}

export interface AdminUpdateInput {
  readonly identifier?: string;
  readonly firstName?: string;
  readonly middleName?: string;
  readonly lastName?: string;
  readonly email?: string;
  readonly phoneNumber?: string;
}

// My Calendar

export interface MyCalendarEvent {
  readonly createdBy: string;
  readonly createdByUserUserName: string;
  readonly createdOn: string;
  // The event this invitation is for + the invitee's role/response (for Accept/Decline).
  readonly eventUid: string;
  readonly role: EventParticipantRole;
  readonly response: InviteResponse;
  readonly respondedAt?: string;
  readonly eventCompanyName: string;
  readonly eventCompanyUid: string;
  readonly eventDepartments: CalendarEventDepartment[];
  readonly eventDescription: string;
  readonly eventEndDate: string;
  readonly eventStartDate: string;
  readonly eventTitle: string;
  readonly eventTypeName: string;
  readonly eventTypeUid: string;
  // Provenance of the underlying event (e.g. "CounsellingRequest") so the calendar can badge it.
  readonly eventOriginType?: string;
  readonly eventOriginUid?: string;
  readonly invitedByUserUid: string;
  readonly invitedByUserUserName: string;
  readonly invitedUsersCount: number;
  readonly isDeleted: boolean;
  readonly rowVersion: string;
  readonly uid: string;
  readonly updatedBy: string;
  readonly updatedByUserUserName: string;
  readonly updatedOn: string;
}

export interface MyCalendarEventsData extends Pagination<MyCalendarEvent> {}

// Platform  Permissions
export interface BasePermission {
  readonly uid: string;
  readonly createdOn: string;
  readonly createdBy: string;
  readonly createdByUserUserName: string;
  readonly updatedOn: string;
  readonly updatedBy: string;
  readonly updatedByUserUserName: string;
  readonly isDeleted: boolean;
  readonly rowVersion: string;
  readonly permissionUid: string;
  readonly permissionName: string;
  readonly allowedAction: string;
}

export interface PlatformPermission extends BasePermission {
  readonly applicationRoleUid: string;
  readonly applicationRoleName: string;
}

export interface PlatformPermissionData
  extends Pagination<PlatformPermission> {}

export interface PlatformPermissionInput {
  readonly uid?: string;
  readonly rowVersion?: string;
  readonly applicationRoleUid?: string;
  readonly permissionUid?: string;
  readonly allowedAction: string;
}

// Admin brief 2026-11-04 — permission matrix (one GET for the whole screen).
// PermissionAction numbers match backend PublicEnums.PermissionAction:
// None=0 / View=100 / Create=200 / Edit=300 / Delete=400. The matrix UI only
// uses 3 states — None / View / Edit — mapping Edit = Delete on write so the
// cell grants full CRUD.
export enum PermissionAction {
  None = 0,
  View = 100,
  Create = 200,
  Edit = 300,
  Delete = 400,
}

export interface PermissionMatrix {
  readonly roles: ReadonlyArray<{ readonly uid: string; readonly name: string }>;
  readonly permissions: ReadonlyArray<{
    readonly uid: string;
    readonly name: string;
    readonly description?: string;
    readonly maxAvailableAction: PermissionAction;
  }>;
  readonly cells: ReadonlyArray<{
    readonly roleUid: string;
    readonly permissionUid: string;
    readonly allowedAction: PermissionAction;
  }>;
}

// Company Permissions
export interface CompanyPermission extends BasePermission {
  readonly companyRoleUid: string;
  readonly companyRoleName: string;
}

export interface CompanyPermissionData extends Pagination<PlatformPermission> {}

export interface CompanyPermissionInput {
  readonly uid?: string;
  readonly rowVersion?: string;
  readonly companyRoleUid?: string;
  readonly permissionUid?: string;
  readonly allowedAction: string;
}

// Permissions

export interface Permission {
  readonly uid: string;
  readonly createdOn: string;
  readonly createdBy: string;
  readonly createdByUserUserName: string;
  readonly updatedOn: string;
  readonly updatedBy: string;
  readonly updatedByUserUserName: string;
  readonly isDeleted: boolean;
  readonly rowVersion: string;
  readonly name: string;
  readonly normalizedName: string;
  readonly description: string;
  readonly permissionName: string;
  readonly permissionUid: string;
  readonly maxAvailableAction: string;
}

export interface PermissionData extends Pagination<Permission> {}

// Platform Roles

export interface PlatformRole {
  readonly uid: string;
  readonly name: string;
  readonly normalizedName: string;
  readonly usersCount: number;
}

export interface PlatformRolesData extends Pagination<PlatformRole> {}

// Invoices

export interface Invoice {
  readonly uid: string;
  readonly isDeleted: boolean;
  readonly rowVersion: string;
  readonly createdOn: string;
  readonly createdBy: string;
  readonly createdByUserUserName: string;
  readonly updatedOn: string;
  readonly updatedBy: string;
  readonly updatedByUserUserName: string;
  readonly companyUid: string;
  readonly companyName: string;
  readonly tenantUid: string;
  readonly tenantName: string;
  readonly description: string;
  readonly dueDate: string;
  readonly totalAmount: number;
  readonly isPaid: boolean;
  readonly itemsCount: number;
}

export interface InvoicesData extends Pagination<Invoice> {}

export interface InvoiceInput {
  readonly uid?: string;
  readonly rowVersion?: string;
  readonly companyUid?: string;
  readonly description?: string;
  readonly dueDate: string;
  readonly totalAmount?: number;
  readonly isPaid: boolean;
}

//Invoice Items
export interface InvoiceItem {
  readonly uid: string;
  readonly createdOn: string;
  readonly createdBy: string;
  readonly createdByUserUserName: string;
  readonly updatedOn: string;
  readonly updatedBy: string;
  readonly updatedByUserUserName: string;
  readonly isDeleted: boolean;
  readonly rowVersion: string;
  readonly description: string;
  readonly quantity: number;
  readonly unitPrice: number;
  readonly vatPercentage: number;
  readonly vatAmount: number;
  readonly total: number;
  readonly invoiceUid: string;
  readonly invoiceCompanyUid: string;
  readonly invoiceTenantUid: string;
}

export interface InvoiceItemsData extends Pagination<InvoiceItem> {}

export interface InvoiceItemInput {
  readonly invoiceUid: string;
  readonly description: string;
  readonly quantity: number;
  readonly unitPrice: number;
  readonly vatPercentage: number;
}

//Bundles
export interface Bundle {
  readonly uid: string;
  readonly createdOn: string;
  readonly createdBy: string;
  readonly createdByUserUserName: string;
  readonly updatedOn: string;
  readonly updatedBy: string;
  readonly updatedByUserUserName: string;
  readonly isDeleted: boolean;
  readonly rowVersion: string;
  readonly name: string;
  readonly normalizedName: string;
  readonly description: string;
  readonly questionnairesCount: number;
  readonly availableLanguagesCount: number;
  readonly assignedCompaniesCount: number;
  readonly testGroupsCount: number;
}

export interface BundlesData extends Pagination<Bundle> {}

export interface BundleInput {
  readonly uid?: string;
  readonly rowVersion?: string;
  readonly name: string;
  readonly description: string;
}

//Bundles Assignments
export interface BundleAssignment {
  readonly companyUid: string;
  readonly companyName: string;
  readonly bundleUid: string;
  readonly bundleName: string;
  readonly uid: string;
  readonly createdOn: string;
  readonly createdBy: string;
  readonly createdByUserUserName: string;
  readonly updatedOn: string;
  readonly updatedBy: string;
  readonly updatedByUserUserName: string;
  readonly rowVersion: string;
  // Soft quota metering: allocated = target the admin set; consumed = answer headers for this
  // (bundle, company). Consumed may exceed allocated (no hard cap — surfaced for manual billing).
  readonly allocatedSubmissions?: number;
  readonly consumedSubmissions: number;
}

export interface BundlesAssignmentsData extends Pagination<BundleAssignment> {}

export interface BundleAssignmentInput {
  readonly uid?: string;
  readonly rowVersion?: string;
  readonly companyUid: string;
  readonly bundleUid: string;
  readonly allocatedSubmissions?: number;
}

// Update carries ONLY the editable field (+ uid/rowVersion) — it maps to BundleCompanyUpdateRequest,
// which has just AllocatedSubmissions. Sending company/bundle would create invalid JSON-Patch paths.
// `null` explicitly clears the allocation (undefined would be dropped by JSON.stringify → no-op).
export interface BundleAssignmentUpdateInput {
  readonly uid?: string;
  readonly rowVersion?: string;
  readonly allocatedSubmissions?: number | null;
}

//Questionnaires
export interface Questionnaire {
  readonly uid: string;
  readonly createdOn: string;
  readonly createdBy: string;
  readonly createdByUserUserName: string;
  readonly updatedOn: string;
  readonly updatedBy: string;
  readonly updatedByUserUserName: string;
  readonly isDeleted: boolean;
  readonly rowVersion: string;
  readonly name: string;
  readonly normalizedName: string;
  readonly type: string;
  readonly description: string;
  readonly duration: number;
  readonly sectionsCount: number;
  readonly bundlesCount: number;
  /** Shared by every version of this questionnaire. */
  readonly lineageUid: string;
  readonly version: number;
  /** Only a Draft is editable; only the Published version is delivered; a Retired one was replaced. */
  readonly status: QuestionnaireStatus;
  readonly publishedAt?: string;
}

export type QuestionnaireStatus = "Draft" | "Published" | "Retired";

export interface QuestionnairesData extends Pagination<Questionnaire> {}

//Budle Questionnaires

export interface BundleQuestionnaire {
  readonly uid: string;
  readonly createdOn: string;
  readonly createdBy: string;
  readonly createdByUserUserName: string;
  readonly updatedOn: string;
  readonly updatedBy: string;
  readonly updatedByUserUserName: string;
  readonly isDeleted: boolean;
  readonly rowVersion: string;
  readonly bundleUid: string;
  readonly bundleName: string;
  readonly questionnaireUid: string;
  readonly questionnaireName: string;
  readonly order: number;
}

export interface BundleQuestionnairesData
  extends Pagination<BundleQuestionnaire> {}

export interface BundleQuestionnaireInput {
  readonly order: number;
}

// Questionnaires
export interface QuestionnaireSection {
  readonly uid: string;
  readonly name: string;
  readonly description: string;
  readonly questionsCount: number;
  readonly questionnaireUid: string;
  readonly order: number;
  readonly rowVersion?: string;
}

export interface QuestionnaireQuestion {
  readonly uid: string;
  readonly description: string;
  readonly questionType: "FreeText" | "SingleChoice" | "MultipleChoice" | "Numeric";
  readonly minValue?: number;
  readonly maxValue?: number;
  readonly unit?: string;
  readonly isRequired: boolean;
  readonly order: number;
  readonly sectionUid: string;
  readonly choicesCount: number;
  readonly translationsCount: number;
  readonly rowVersion?: string;
}

export interface QuestionnaireQuestionChoice {
  readonly uid: string;
  readonly description: string;
  readonly order: number;
  readonly weight: number;
  readonly questionUid: string;
  readonly translationsCount: number;
  readonly rowVersion?: string;
}

export interface QuestionnaireQuestionTranslation {
  readonly uid: string;
  readonly translatedDescription: string;
  readonly languageUid: string;
  readonly languageName: string;
  readonly questionUid: string;
  readonly rowVersion: string;
}

export interface QuestionnaireQuestionChoiceTranslation {
  readonly uid: string;
  readonly translatedDescription: string;
  readonly languageUid: string;
  readonly rowVersion: string;
}

export interface QuestionnaireSectionsData
  extends Pagination<QuestionnaireSection> {}
export interface QuestionnaireQuestionsData
  extends Pagination<QuestionnaireQuestion> {}
export interface QuestionnaireQuestionChoicesData
  extends Pagination<QuestionnaireQuestionChoice> {}
export interface QuestionnaireQuestionTranslationsData
  extends Pagination<QuestionnaireQuestionTranslation> {}
export interface QuestionnaireQuestionChoiceTranslationInput {
  readonly uid?: string;
  readonly translatedDescription: string;
  readonly questionnaireQuestionChoiceUid: string;
  readonly languageUid: string;
  readonly rowVersion?: string;
}

// Input interfaces
export interface QuestionnaireInput {
  readonly uid?: string;
  readonly name: string;
  readonly description?: string;
  readonly type: "Test" | "Survey" | "Roadmap";
  readonly duration: number;
  readonly rowVersion?: string;
}

export interface QuestionnaireSectionInput {
  readonly uid?: string;
  readonly name: string;
  readonly description?: string;
  readonly questionnaireUid?: string;
  readonly order: number;
  readonly rowVersion?: string;
}

export interface QuestionnaireQuestionInput {
  readonly uid?: string;
  readonly description: string;
  readonly questionType: "FreeText" | "SingleChoice" | "MultipleChoice" | "Numeric";
  readonly minValue?: number;
  readonly maxValue?: number;
  readonly unit?: string;
  readonly isRequired?: boolean;
  readonly order: number;
  readonly rowVersion?: string;
}

export interface QuestionnaireQuestionChoiceInput {
  readonly uid?: string;
  readonly description: string;
  readonly weight: number;
  readonly order: number;
  readonly rowVersion?: string;
}

export interface QuestionnaireQuestionTranslationInput {
  readonly uid?: string;
  readonly translatedDescription: string;
  readonly questionnaireQuestionUid: string;
  readonly languageUid: string;
  readonly rowVersion?: string;
}

export interface QuestionnaireQuestionChoiceTranslationInput {
  readonly uid?: string;
  readonly translatedDescription: string;
  readonly questionnaireQuestionChoiceUid: string;
  readonly languageUid: string;
  readonly rowVersion?: string;
}

export interface QuestionnaireQuestionChoiceTranslationsData
  extends Pagination<QuestionnaireQuestionChoiceTranslation> {}

// TestGroups

export interface TestGroup {
  readonly uid: string;
  readonly createdOn: string;
  readonly createdBy: string;
  readonly createdByUserUserName: string;
  readonly updatedOn: string;
  readonly updatedBy: string;
  readonly updatedByUserUserName: string;
  readonly isDeleted: boolean;
  readonly rowVersion: string;
  readonly companyUid: string;
  readonly companyName: string;
  readonly bundleUid: string;
  readonly bundleName: string;
  readonly allowedSubmissions: number;
  readonly usersCount: number;
}

export interface TestGroupData extends Pagination<TestGroup> {}

export interface TestGroupInput {
  readonly uid?: string;
  readonly rowVersion?: string;
  readonly bundleUid?: string;
  readonly companyUid?: string;
  readonly allowedSubmissions: number;
}

export interface TestGroupUser {
  readonly uid: string;
  readonly createdOn: string;
  readonly createdBy: string;
  readonly createdByUserUserName: string;
  readonly updatedOn: string;
  readonly updatedBy: string;
  readonly updatedByUserUserName: string;
  readonly isDeleted: boolean;
  readonly rowVersion: string;
  readonly testGroupUid: string;
  readonly userUid: string;
  readonly userUserName: string;
}

export interface TestGroupUserData extends Pagination<TestGroupUser> {}

// Questionnaire Answers
export interface Answer {
  readonly uid: string;
  readonly createdBy: string;
  readonly createdByUserUserName: string;
  readonly updatedOn: string;
  readonly updatedBy: string;
  readonly updatedByUserUserName: string;
  readonly isDeleted: boolean;
  readonly rowVersion: string;
  readonly userUid: string;
  readonly userUserName: string;
  readonly questionnaireUid: string;
  readonly questionnaireName: string;
  /** The questionnaire across its versions, and the version this answer was given on. */
  readonly questionnaireLineageUid?: string;
  readonly questionnaireVersion?: number;
  readonly bundleUid: string;
  readonly bundleName: string;
  readonly companyUid: string;
  readonly companyName: string;
  readonly tenantUid: string;
  readonly tenantName: string;
  readonly questionAnswersCount: number;
  readonly completed?: boolean;
  /** When the answer was completed — its reporting date. Absent while in progress. */
  readonly completedAt?: string;
  readonly scoringCompleted?: boolean;
  /** The scorer gave up after its bounded attempts (completed, never scored). Display-only. */
  readonly scoringFailed?: boolean;
  readonly scoringAttempts?: number;
  readonly createdOn: string;
}

export interface AnswerData extends Pagination<Answer> {}

// Questionnaire answer KPI results (computed by the scoring engine)
export interface QuestionnaireAnswerKPIResult {
  readonly uid: string;
  readonly createdOn: string;
  readonly createdBy: string;
  readonly createdByUserUserName: string;
  readonly updatedOn: string;
  readonly updatedBy: string;
  readonly updatedByUserUserName: string;
  readonly isDeleted: boolean;
  readonly rowVersion: string;
  readonly questionnaireAnswerUid: string;
  readonly kpiDefinitionUid: string;
  readonly kpiDefinitionName: string;
  readonly score: number;
  readonly resultingRangeUid?: string;
  readonly resultingRangeLabel?: string;
  readonly resultingRangeColor?: string;
  // Populated from the parent answer by the results read; may be null when an
  // answer has no company / department
  readonly companyUid?: string;
  readonly companyName?: string;
  readonly userUid?: string;
  readonly userUserName?: string;
  readonly departmentUid?: string;
  readonly departmentName?: string;
  readonly assetUid?: string;
  readonly assetName?: string;
  readonly questionnaireUid?: string;
  readonly questionnaireName?: string;
  readonly answerCreatedOn?: string;
  // Target-comparison enrichment (returned by the enriched KPI-result read).
  readonly targetValue?: number;
  readonly tolerance?: number;
  readonly minValue?: number;
  readonly maxValue?: number;
  readonly targetPct?: number;
  readonly companyValue?: number;
  readonly vsTargetDelta?: number;
  readonly vsTargetBand?: string;
  readonly vsTargetColor?: string;
}

export interface QuestionnaireAnswerKPIResultsData
  extends Pagination<QuestionnaireAnswerKPIResult> {}

export interface QuestionAnswer {
  readonly uid: string;
  readonly createdOn: string;
  readonly createdBy: string;
  readonly createdByUserUserName: string;
  readonly updatedOn: string;
  readonly updatedBy: string;
  readonly updatedByUserUserName: string;
  readonly isDeleted: boolean;
  readonly rowVersion: string;
  readonly questionnaireAnswerUid: string;
  readonly sectionUid: string;
  readonly userUid: string;
  readonly userUserName: string;
  readonly questionUid: string;
  readonly questionDescription: string;
  readonly freeTextAnswer: string;
  readonly numericAnswer?: number;
  readonly selectedChoices: string[];
}

export interface QuestionAnswerData extends Pagination<QuestionAnswer> {}

//Company Collaborators

export interface CompanyCollaborator {
  readonly uid: string;
  readonly createdOn: string;
  readonly createdBy: string;
  readonly createdByUserUserName: string;
  readonly updatedOn: string;
  readonly updatedBy: string;
  readonly updatedByUserUserName: string;
  readonly isDeleted: boolean;
  readonly rowVersion: string;
  readonly companyUid: string;
  readonly companyName: string;
  readonly userUid: string;
  readonly userUserName: string;
}

export interface CompanyCollaboratorsData
  extends Pagination<CompanyCollaborator> {}

export interface CompanyCollaboratorInput {
  readonly uid?: string;
  readonly rowVersion?: string;
  readonly companyUid: string;
  readonly userUid: string;
}

//Reports
export enum WellbeingReportStatus {
  Draft = "Draft",
  Submitted = "Submitted",
  Appraised = "Appraised",
}

export enum ReportAppraisalVerdict {
  Pending = "Pending",
  Approved = "Approved",
  ChangesRequested = "ChangesRequested",
}

export interface WellbeingReport {
  readonly uid: string;
  readonly createdOn: string;
  readonly createdBy: string;
  readonly createdByUserUserName: string;
  readonly updatedOn: string;
  readonly updatedBy: string;
  readonly updatedByUserUserName: string;
  readonly isDeleted: boolean;
  readonly rowVersion: string;
  readonly summary: string;
  readonly status: WellbeingReportStatus;
  readonly submittedOn?: string;
  readonly reportingForUserUid: string;
  readonly reportingForUserUserName: string;
  readonly companyUid: string;
  readonly companyName: string;
  readonly departmentUid?: string;
  readonly departmentName?: string;
  readonly assetUid?: string;
  readonly assetName?: string;
  readonly reviewersCount: number;
  readonly filesCount: number;
}

export interface WellbeingReportsData
  extends Pagination<WellbeingReport> {}

export interface WellbeingReportInput {
  readonly uid?: string;
  readonly rowVersion?: string;
  readonly summary: string;
  readonly reportingForUserUid?: string;
  readonly companyUid: string;
  readonly departmentUid?: string;
  readonly assetUid?: string;
}

// Lightweight target options for the report authoring pickers (scoped server-side to the
// author's reachable companies — admin all, company user own + sub-companies, affiliate assigned).
export interface ReportTargetOption {
  readonly uid: string;
  readonly name: string;
  readonly code?: string;
}

// Report Files
export interface ReportFile {
  readonly uid: string;
  readonly createdOn: string;
  readonly createdBy: string;
  readonly createdByUserUserName: string;
  readonly updatedOn: string;
  readonly updatedBy: string;
  readonly updatedByUserUserName: string;
  readonly isDeleted: boolean;
  readonly rowVersion: string;
  readonly fileName: string;
  readonly fileExtension: string;
  readonly estimatedSizeInBytes: number;
  readonly reportUid: string;
}

export interface ReportFilesData extends Pagination<ReportFile> {}

//Report Reviewers

export interface Reviewer {
  readonly uid: string;
  readonly createdOn: string;
  readonly createdBy: string;
  readonly createdByUserUserName: string;
  readonly updatedOn: string;
  readonly updatedBy: string;
  readonly updatedByUserUserName: string;
  readonly isDeleted: boolean;
  readonly rowVersion: string;
  readonly notes: string;
  readonly verdict: ReportAppraisalVerdict;
  readonly appraisedOn?: string;
  readonly reportUid: string;
  readonly companyUid: string;
  readonly companyName: string;
  readonly userUid: string;
  readonly userUserName: string;
}

export interface ReviewersData extends Pagination<Reviewer> {}

//Counselling Requests

// Lean negotiation status: counter-propose = re-Propose (proposedByUid tells whose turn it is to Agree).
export enum CounsellingRequestStatus {
  Requested = "Requested",
  Proposed = "Proposed",
  Scheduled = "Scheduled",
  Completed = "Completed",
  Cancelled = "Cancelled",
}

export interface CounsellingRequest {
  readonly uid: string;
  readonly createdOn: string;
  readonly createdBy: string;
  readonly createdByUserUserName: string;
  readonly updatedOn: string;
  readonly updatedBy: string;
  readonly updatedByUserUserName: string;
  readonly isDeleted: boolean;
  readonly rowVersion: string;
  readonly status: CounsellingRequestStatus;
  readonly reason?: string;
  readonly requestedByUid?: string;
  readonly requestedByUserName?: string;
  readonly therapistUid: string;
  readonly therapistUserName: string;
  readonly patientUid: string;
  readonly patientUserName: string;
  // Slot currently under negotiation + who proposed it (whose turn it is for the other party to Agree).
  readonly proposedAt?: string;
  readonly proposedByUid?: string;
  // Finalized session time (set on Agree) + the backing calendar event.
  readonly scheduledAt?: string;
  readonly eventUid?: string;
  // Company/tenant scope (from CompanyStoreableDTO). Counselling has no department of its own —
  // the grid's Department picker only narrows the patient lookup, it isn't carried on the record.
  readonly companyUid: string;
  readonly companyName: string;
}

export interface CounsellingRequestsData
  extends Pagination<CounsellingRequest> {}

// Create: a requester raises a request targeting a therapist, optionally with a reason and a first proposed slot.
export interface CounsellingRequestInput {
  readonly therapistUid: string;
  readonly patientUid?: string;
  readonly reason?: string;
  readonly proposedAt?: Date;
}

// Generic edits to a request's particulars before scheduling (status moves via the lifecycle actions, not here).
export interface CounsellingRequestUpdateInput {
  readonly uid?: string;
  readonly rowVersion?: string;
  readonly therapistUid?: string;
  readonly patientUid?: string;
  readonly reason?: string;
}

// Body for a (counter-)proposal of a session slot.
export interface CounsellingProposeInput {
  readonly proposedAt: Date;
}

// Trainings

// Lean lifecycle, no negotiation: planning a training immediately creates its backing calendar event;
// it is then either completed by its host (attendance marked) or cancelled (event torn down).
export enum TrainingStatus {
  Planned = "Planned",
  Completed = "Completed",
  Cancelled = "Cancelled",
}

export interface Training {
  readonly uid: string;
  readonly createdOn: string;
  readonly createdBy: string;
  readonly createdByUserUserName: string;
  readonly updatedOn: string;
  readonly updatedBy: string;
  readonly updatedByUserUserName: string;
  readonly isDeleted: boolean;
  readonly rowVersion: string;
  readonly status: TrainingStatus;
  readonly title: string;
  readonly description?: string;
  readonly location?: string;
  readonly startDate: string;
  readonly endDate: string;
  // Facilitating affiliate (consultant/collaborator); affiliates always host their own trainings.
  readonly hostUid?: string;
  readonly hostUserName?: string;
  readonly completedAt?: string;
  // PD-01 §10 — host's post-training write-up, surfaced in Training Reports.
  readonly feedback?: string | null;
  // The backing calendar event; attendees are invited through the standard event-invitation endpoints.
  readonly eventUid?: string;
  // Company/tenant scope (from CompanyStoreableDTO) — the company the training is delivered to.
  readonly companyUid?: string;
  readonly companyName?: string;
  readonly tenantUid?: string;
  readonly tenantName?: string;
}

export interface TrainingsData extends Pagination<Training> {}

// Create: plan a training against a company (the backing event is created server-side). Admins name the
// facilitating affiliate; affiliates are auto-set as their own host.
export interface TrainingInput {
  readonly title: string;
  readonly description?: string;
  readonly location?: string;
  readonly startDate: Date;
  readonly endDate: Date;
  readonly companyUid: string;
  readonly hostUid?: string;
}

// Full-replace edit (PUT) of a planned training's particulars; the backing event syncs server-side.
// Non-admins must send the unchanged hostUid — changing the host is admin-only.
export interface TrainingUpdateInput {
  readonly title: string;
  readonly description?: string;
  readonly location?: string;
  readonly startDate: Date;
  readonly endDate: Date;
  readonly hostUid?: string;
  readonly feedback?: string | null;
  readonly rowVersion: string;
}

// One attendance mark against an invite of the training's backing event.
export interface TrainingAttendanceItem {
  readonly inviteUid: string;
  readonly attended: boolean;
}

// Loyalty History
export interface LoyaltyHistory {
  readonly uid: string;
  readonly createdOn: string;
  readonly createdBy: string;
  readonly createdByUserUserName: string;
  readonly updatedOn: string;
  readonly updatedBy: string;
  readonly updatedByUserUserName: string;
  readonly isDeleted: boolean;
  readonly rowVersion: string;
  readonly pointsChange: number;
  readonly description: string;
  readonly userUid: string;
  readonly userUserName: string;
  readonly companyUid: string;
  readonly companyName: string;
  readonly departmentUid?: string;
  readonly departmentName?: string;
  readonly tenantUid: string;
  readonly tenantName: string;
  readonly rewardUid: string;
  readonly rewardName: string;
}

export interface LoyaltyHistoryData extends Pagination<LoyaltyHistory> {}

// KPIs

export interface Kpi {
  readonly uid: string;
  readonly createdOn: string;
  readonly createdBy: string;
  readonly createdByUserUserName: string;
  readonly updatedOn: string;
  readonly updatedBy: string;
  readonly updatedByUserUserName: string;
  readonly isDeleted: boolean;
  readonly rowVersion: string;
  readonly name: string;
  readonly normalizedName: string;
  readonly description: string;
  readonly shortDescription: string;
  readonly type: KpiType;
  readonly scoringMethod: KpiScoringMethodType;
  readonly reportKPIReferenceMatrixUid: string;
  readonly targetedQuestionsCount: number;
  readonly linkedReportsCount: number;
  /** The questionnaire version that owns the KPI — absent until its first question is linked. */
  readonly questionnaireUid?: string | null;
  readonly questionnaireName?: string | null;
  readonly questionnaireVersion?: number | null;
  readonly questionnaireStatus?: QuestionnaireStatus | null;
}

export interface KpisData extends Pagination<Kpi> {}

// Assessment Reports (a named lens = a curated set of KPIs)

export interface AssessmentReport {
  readonly uid: string;
  readonly createdOn: string;
  readonly createdBy: string;
  readonly createdByUserUserName: string;
  readonly updatedOn: string;
  readonly updatedBy: string;
  readonly updatedByUserUserName: string;
  readonly isDeleted: boolean;
  readonly rowVersion: string;
  readonly name: string;
  readonly normalizedName: string;
  readonly description: string;
  readonly shortDescription: string;
  // Serialized from the DTO's `KPICount` (camelCase lowercases the leading KPI run -> kpiCount)
  readonly kpiCount: number;
  // Base = a KPI bundle; Meta = a computed composite (Gap / WeightedIndex over components).
  readonly kind: "Base" | "Meta";
  readonly compositeOperator?: "Gap" | "WeightedIndex" | null;
  readonly componentCount: number;
}

export interface AssessmentReportData extends Pagination<AssessmentReport> {}

export interface ReportKpiDefinition {
  readonly uid: string;
  readonly reportUid: string;
  readonly reportName: string;
  // DTO props KPIDefinitionUid/Name -> kpiDefinitionUid/Name (camelCase lowercases the leading KPI run)
  readonly kpiDefinitionUid: string;
  readonly kpiDefinitionName: string;
}

export interface ReportKpiDefinitionData extends Pagination<ReportKpiDefinition> {}

export interface AssessmentReportInput {
  readonly uid?: string;
  readonly name: string;
  readonly shortDescription: string;
  readonly description: string;
  readonly kind?: "Base" | "Meta";
  readonly compositeOperator?: "Gap" | "WeightedIndex" | null;
  readonly rowVersion?: string;
}

export interface ReportKpiDefinitionInput {
  readonly reportUid: string;
  // Matches the ReportKPIDefinitionCreateRequest's KPIDefinitionUid -> kpiDefinitionUid
  readonly kpiDefinitionUid: string;
}

// ----- Meta-report composition (components + bands) + evaluation -----

export type ReportComponentInputType = "Kpi" | "Metric" | "Report";
export type ReportComponentRole = "Term" | "Minuend" | "Subtrahend";

export interface ReportComponent {
  readonly uid: string;
  readonly rowVersion: string;
  readonly reportUid: string;
  readonly inputType: ReportComponentInputType;
  readonly inputKpiDefinitionUid?: string | null;
  readonly inputMetricDefinitionUid?: string | null;
  readonly inputReportUid?: string | null;
  readonly weight: number;
  readonly role: ReportComponentRole;
  readonly normalize: boolean;
  readonly order: number;
}
export interface ReportComponentData extends Pagination<ReportComponent> {}
export interface ReportComponentInput {
  readonly uid?: string;
  readonly rowVersion?: string;
  readonly reportUid?: string;
  readonly inputType?: ReportComponentInputType;
  readonly inputKpiDefinitionUid?: string | null;
  readonly inputMetricDefinitionUid?: string | null;
  readonly inputReportUid?: string | null;
  readonly weight?: number;
  readonly role?: ReportComponentRole;
  readonly normalize?: boolean;
  readonly order?: number;
}

export interface ReportBand {
  readonly uid: string;
  readonly rowVersion: string;
  readonly reportUid: string;
  readonly label: string;
  readonly upperThreshold: number;
  readonly color?: string;
  readonly interpretation?: string;
  readonly recommendedAction?: string;
}
export interface ReportBandData extends Pagination<ReportBand> {}
export interface ReportBandInput {
  readonly uid?: string;
  readonly rowVersion?: string;
  readonly reportUid?: string;
  readonly label?: string;
  readonly upperThreshold?: number;
  readonly color?: string;
  readonly interpretation?: string;
  readonly recommendedAction?: string;
}

export interface BandScaleEntry {
  readonly label?: string | null;
  readonly threshold: number;
  readonly color?: string | null;
  readonly interpretation?: string | null;
  readonly recommendedAction?: string | null;
  readonly isResolved: boolean;
}
export interface ReportComponentResult {
  readonly label: string;
  readonly inputType: ReportComponentInputType;
  readonly inputUid?: string | null;
  // The KPI row's lineage: its results across every version (inputUid is the version it reads on).
  readonly inputLineageUid?: string | null;
  readonly weight: number;
  readonly value?: number | null;
  readonly bandLabel?: string | null;
  readonly bandColor?: string | null;
  readonly contribution?: number | null;
  // Rich-PDF enrichment (also available to the on-screen view if we choose to surface it).
  readonly ownerDescription?: string | null;
  readonly ownerShortDescription?: string | null;
  readonly bandScale?: BandScaleEntry[];
  readonly resolvedInterpretation?: string | null;
  readonly recommendedAction?: string | null;
  // Target-comparison enrichment (returned by the report-evaluation read).
  readonly targetValue?: number;
  readonly targetPct?: number;
  readonly companyValue?: number;
  readonly vsTargetDelta?: number;
  readonly vsTargetBand?: string;
  readonly vsTargetColor?: string;
}
export interface ReportDomainGap {
  readonly domain: string;
  readonly minuendValue?: number | null;
  readonly subtrahendValue?: number | null;
  readonly gap?: number | null;
  readonly bandLabel?: string | null;
  readonly bandColor?: string | null;
  readonly recommendation?: string | null;
}
export interface ReportEvaluation {
  readonly reportUid: string;
  readonly reportName: string;
  readonly kind: "Base" | "Meta";
  readonly compositeOperator?: "Gap" | "WeightedIndex" | null;
  readonly value?: number | null;
  readonly unit?: string | null;
  readonly bandLabel?: string | null;
  readonly bandColor?: string | null;
  readonly bandInterpretation?: string | null;
  readonly bandRecommendedAction?: string | null;
  readonly components: ReportComponentResult[];
  readonly domainGaps: ReportDomainGap[];
  readonly scopeCompanyUid: string;
  readonly scopeCompanyName?: string | null;
  readonly scopeFiscalPeriodUid?: string | null;
  readonly scopeFiscalPeriodName?: string | null;
  readonly scopeDepartmentUid?: string | null;
  readonly scopeDepartmentName?: string | null;
  readonly respondentCount: number;
  readonly note?: string | null;
  /** Set when the period's answers span more than one version of a questionnaire (names versions + counts). */
  readonly versionNotice?: string | null;
}

// ----- F-10 Organizational metrics -----

export type MetricType = "Absenteeism" | "Turnover" | "Grievance" | "Accident";

export interface MetricBand {
  readonly uid: string;
  readonly rowVersion: string;
  readonly label: string;
  readonly upperThreshold: number;
  readonly color?: string;
  readonly readinessMultiplier: number;
  readonly metricDefinitionUid: string;
  readonly interpretation?: string;
  readonly recommendedAction?: string;
}
export interface MetricBandData extends Pagination<MetricBand> {}
export interface MetricBandInput {
  readonly uid?: string;
  readonly rowVersion?: string;
  readonly label?: string;
  readonly upperThreshold?: number;
  readonly color?: string;
  readonly readinessMultiplier?: number;
  readonly metricDefinitionUid?: string;
  readonly interpretation?: string;
  readonly recommendedAction?: string;
}

export interface MetricDefinition {
  readonly uid: string;
  readonly rowVersion: string;
  readonly name: string;
  readonly normalizedName: string;
  readonly description: string;
  readonly metricType: MetricType;
  readonly unit?: string;
  readonly baseWeight: number;
  readonly higherIsWorse: boolean;
  readonly bands: MetricBand[];
}
export interface MetricDefinitionData extends Pagination<MetricDefinition> {}
export interface MetricDefinitionInput {
  readonly uid?: string;
  readonly rowVersion?: string;
  readonly name?: string;
  readonly description?: string;
  readonly unit?: string;
  readonly baseWeight?: number;
  readonly higherIsWorse?: boolean;
}

export interface OrganizationalMetricEntry {
  readonly uid: string;
  readonly rowVersion: string;
  readonly companyUid: string;
  readonly companyName?: string;
  readonly departmentUid: string;
  readonly departmentName?: string;
  readonly companyFiscalPeriodUid: string;
  readonly companyFiscalPeriodName?: string;
  readonly metricType: MetricType;
  readonly headcount: number;
  readonly scheduledWorkdays?: number | null;
  readonly eventCount: number;
  readonly voluntaryDepartures?: number | null;
  readonly involuntaryDepartures?: number | null;
  readonly retirements?: number | null;
  readonly rate: number;
}
export interface OrganizationalMetricEntryData
  extends Pagination<OrganizationalMetricEntry> {}
export interface OrganizationalMetricEntryInput {
  readonly uid?: string;
  readonly rowVersion?: string;
  readonly companyUid?: string;
  readonly departmentUid?: string;
  readonly companyFiscalPeriodUid?: string;
  readonly metricType?: MetricType;
  readonly headcount?: number;
  readonly scheduledWorkdays?: number | null;
  readonly eventCount?: number;
  readonly voluntaryDepartures?: number | null;
  readonly involuntaryDepartures?: number | null;
  readonly retirements?: number | null;
}

export interface KpiInput {
  readonly uid?: string;
  readonly rowVersion?: string;
  readonly name: string;
  readonly description: string;
  readonly shortDescription: string;
  readonly type: string;
  readonly scoringMethod: string;
}

// KPIs Questions
export interface KpiQuestion {
  readonly uid: string;
  readonly createdOn: string;
  readonly createdBy: string;
  readonly createdByUserUserName: string;
  readonly updatedOn: string;
  readonly updatedBy: string;
  readonly updatedByUserUserName: string;
  readonly isDeleted: boolean;
  readonly rowVersion: string;
  readonly questionUid: string;
  readonly kpiDefinitionUid: string;
  readonly kpiDefinitionName: string;
  readonly questionDescription: string;
  readonly questionOrder: number;
}

export interface KpisQuestionsData extends Pagination<KpiQuestion> {}

export interface KpiQuestionInput {
  readonly uid?: string;
  readonly rowVersion?: string;
  readonly questionUid: string;
  readonly kpiDefinitionUid: string;
}

// KPIs Matrices

export interface KpiMatrix {
  readonly uid: string;
  readonly createdOn: string;
  readonly createdBy: string;
  readonly createdByUserUserName: string;
  readonly updatedOn: string;
  readonly updatedBy: string;
  readonly updatedByUserUserName: string;
  readonly isDeleted: boolean;
  readonly rowVersion: string;
  readonly expectedValue: number;
  readonly standardDeviation: number;
  readonly minValue: number;
  readonly maxValue: number;
  readonly kpiDefinitionUid: string;
  readonly bucketsCount: number;
}

export interface KpiMatricesData extends Pagination<KpiMatrix> {}

export interface KpiMatrixInput {
  readonly uid?: string;
  readonly kpiDefinitionUid?: string;
  readonly rowVersion?: string;
  // Optional "expected norm" benchmark (drives a future vs-expected report comparison).
  readonly expectedValue?: number | null;
  readonly standardDeviation?: number | null;
  // The raw-score scale; report % = (score − minValue) / (maxValue − minValue) × 100.
  readonly minValue: number;
  readonly maxValue: number;
}

//KPIs Ranges
export interface Range {
  readonly uid: string;
  readonly createdOn: string;
  readonly createdBy: string;
  readonly createdByUserUserName: string;
  readonly updatedOn: string;
  readonly updatedBy: string;
  readonly updatedByUserUserName: string;
  readonly isDeleted: boolean;
  readonly rowVersion: string;
  readonly label: string;
  readonly threshold: number;
  readonly color: string;
  readonly matrixUid: string;
  readonly kpiDefinitionUid: string;
  readonly interpretation?: string;
  readonly recommendedAction?: string;
}

export interface RangesData extends Pagination<Range> {}

export interface RangeInput {
  readonly uid?: string;
  readonly rowVersion?: string;
  readonly matrixUid: string;
  readonly label: string;
  readonly color: string;
  readonly threshold: number;
  readonly interpretation?: string;
  readonly recommendedAction?: string;
}

// Question
export interface Question {
  readonly uid: string;
  readonly createdOn: string;
  readonly createdBy: string;
  readonly createdByUserUserName: string;
  readonly updatedOn: string;
  readonly updatedBy: string;
  readonly updatedByUserUserName: string;
  readonly isDeleted: boolean;
  readonly rowVersion: string;
  readonly questionType: "FreeText" | "SingleChoice" | "MultipleChoice" | "Numeric";
  readonly minValue?: number;
  readonly maxValue?: number;
  readonly unit?: string;
  readonly isRequired: boolean;
  readonly description: string;
  readonly sectionUid: string;
  readonly sectionName: string;
  readonly order: number;
  readonly choicesCount: number;
  readonly questionnaireUid?: string;
  readonly questionnaireName?: string;
  readonly questionnaireVersion?: number;
  readonly questionnaireStatus?: QuestionnaireStatus;
}

// User Input Types

export interface UserProfileInput {
  readonly uid?: string;
  readonly rowVersion?: string;
  readonly firstName?: string;
  readonly middleName?: string;
  readonly lastName?: string;
  readonly email?: string;
  readonly phoneNumber?: string;
  readonly companyPhoneNumber?: string;
  readonly dateOfBirth?: string;
  readonly aboutMe?: string;
  readonly gender?: string;
  readonly testLanguageUid?: string;
  readonly countryUid?: string;
  readonly lockoutEnabled?: boolean;
  readonly internalId?: number;
  readonly externalId?: string;
  readonly assetUid?: string;
  // Organisation placement, editable since unit F. The API validates all three against the user's own
  // company and refuses anything belonging to another one.
  readonly departmentUid?: string;
  readonly roleUid?: string;
}

// ── Processes (Roadmaps) ──────────────────────────────────────────────────────
// Admins author a reusable "wellbeing process" template: Roadmap → ordered
// Stages → ordered Requirements. Each Requirement gates exactly ONE manager action
// (RoadmapLevelAction, single-select) and — for AnswerQuestionnaire / DistributeBundle —
// links the assignable Questionnaire / Bundle. Stages carry .xls/.xlsx resource files at
// template- and per-company level; a RoadmapCompany assigns a roadmap to a company and
// tracks CurrentStage/Requirement.

// Mirrors the backend RoadmapLevelAction enum (Common/Definitions/PublicEnums.cs). The API serializes
// enums by NAME (JsonStringEnumConverter), so availableActions is the string member name, not a number.
export const RoadmapLevelAction = {
  None: "None",
  AnswerQuestionnaire: "AnswerQuestionnaire",
  DistributeBundle: "DistributeBundle",
  CreatePolicy: "CreatePolicy",
  CreateProgram: "CreateProgram",
} as const;

// AnswerQuestionnaire/DistributeBundle drive an entity picker (`entity`); the rest are
// informational authoring steps with no linked entity.
export const RoadmapLevelActionOptions: readonly {
  readonly value: string;
  readonly label: string;
  readonly entity?: "questionnaire" | "bundle";
}[] = [
  { value: RoadmapLevelAction.AnswerQuestionnaire, label: "Answer Questionnaire", entity: "questionnaire" },
  { value: RoadmapLevelAction.DistributeBundle, label: "Distribute Bundle", entity: "bundle" },
  { value: RoadmapLevelAction.CreatePolicy, label: "Create Policy" },
  { value: RoadmapLevelAction.CreateProgram, label: "Create Program" },
];

export function roadmapActionLabel(value: string | null | undefined): string {
  return RoadmapLevelActionOptions.find((o) => o.value === value)?.label ?? "—";
}

export interface Roadmap {
  readonly uid: string;
  readonly createdOn: string;
  readonly createdBy: string;
  readonly createdByUserUserName: string;
  readonly updatedOn: string;
  readonly updatedBy: string;
  readonly updatedByUserUserName: string;
  readonly isDeleted: boolean;
  readonly rowVersion: string;
  readonly name: string;
  readonly normalizedName: string;
  readonly description: string;
  readonly shortDescription: string;
  readonly stepsDescription: string;
  readonly isActive: boolean;
  readonly stagesCount: number;
  readonly companiesCount: number;
}

export interface RoadmapsData extends Pagination<Roadmap> {}

export interface RoadmapInput {
  readonly uid?: string;
  readonly rowVersion?: string;
  readonly name: string;
  readonly description?: string;
  readonly shortDescription?: string;
  readonly stepsDescription?: string;
}

export interface RoadmapStage {
  readonly uid: string;
  readonly createdOn: string;
  readonly createdBy: string;
  readonly createdByUserUserName: string;
  readonly updatedOn: string;
  readonly updatedBy: string;
  readonly updatedByUserUserName: string;
  readonly isDeleted: boolean;
  readonly rowVersion: string;
  readonly name: string;
  readonly normalizedName: string;
  readonly order: number;
  readonly isPublished: boolean;
  readonly description: string;
  readonly shortDescription: string;
  readonly stepsDescription: string;
  readonly roadmapUid: string;
  readonly roadmapName: string;
}

export interface RoadmapStagesData extends Pagination<RoadmapStage> {}

export interface RoadmapStageInput {
  readonly uid?: string;
  readonly rowVersion?: string;
  readonly name: string;
  readonly order: number;
  readonly isPublished?: boolean;
  readonly description?: string;
  readonly shortDescription?: string;
  readonly stepsDescription?: string;
  // sent on add only — scopes the new stage to its parent roadmap
  readonly roadmapUid?: string;
}

export interface RoadmapRequirement {
  readonly uid: string;
  readonly createdOn: string;
  readonly createdBy: string;
  readonly createdByUserUserName: string;
  readonly updatedOn: string;
  readonly updatedBy: string;
  readonly updatedByUserUserName: string;
  readonly isDeleted: boolean;
  readonly rowVersion: string;
  readonly name: string;
  readonly normalizedName: string;
  readonly order: number;
  readonly isPublished: boolean;
  readonly description: string;
  readonly shortDescription: string;
  readonly stepsDescription: string;
  readonly roadmapLevelUid: string;
  readonly roadmapLevelName: string;
  readonly availableActions: string | null;
  readonly assignableBundleUid?: string | null;
  readonly assignableBundleName?: string | null;
  readonly assignableQuestionnaireUid?: string | null;
  readonly assignableQuestionnaireName?: string | null;
  readonly assignableQuestionnaireLineageUid?: string | null;
}

export interface RoadmapRequirementsData extends Pagination<RoadmapRequirement> {}

export interface RoadmapRequirementInput {
  readonly uid?: string;
  readonly rowVersion?: string;
  readonly name: string;
  readonly order: number;
  readonly isPublished?: boolean;
  readonly description?: string;
  readonly shortDescription?: string;
  readonly stepsDescription?: string;
  readonly availableActions?: string | null;
  readonly assignableBundleUid?: string | null;
  readonly assignableQuestionnaireUid?: string | null;
  // sent on add only — scopes the new requirement to its parent stage
  readonly roadmapLevelUid?: string;
}

export interface RoadmapCompany {
  readonly uid: string;
  readonly createdOn: string;
  readonly createdBy: string;
  readonly createdByUserUserName: string;
  readonly updatedOn: string;
  readonly updatedBy: string;
  readonly updatedByUserUserName: string;
  readonly isDeleted: boolean;
  readonly rowVersion: string;
  readonly roadmapUid: string;
  readonly roadmapName: string;
  readonly companyUid: string;
  readonly companyName: string;
  readonly currentStage: number;
  readonly currentRequirement: number;
}

export interface RoadmapCompaniesData extends Pagination<RoadmapCompany> {}

export interface RoadmapCompanyInput {
  readonly uid?: string;
  readonly rowVersion?: string;
  // sent on add only
  readonly roadmapUid?: string;
  readonly companyUid?: string;
  // progress edit (PATCH)
  readonly currentStage?: number;
  readonly currentRequirement?: number;
}

// Roadmap files (template stage files + per-company files) were removed from the MVP — see
// data.repository.ts. Their types are intentionally gone; re-add if files are re-introduced.

// ============================================================================
// WellBeing Corner
// ============================================================================

// Serialized by NAME on the backend (WellbeingMaterialType enum) — use the string values.
export type WellbeingMaterialType = "Article" | "Video" | "Document" | "Link";

export interface WellbeingMaterial {
  readonly uid: string;
  readonly createdOn: string;
  readonly createdBy: string;
  readonly createdByUserUserName: string;
  readonly updatedOn: string;
  readonly updatedBy: string;
  readonly updatedByUserUserName: string;
  readonly isDeleted: boolean;
  readonly rowVersion: string;
  readonly name: string;
  readonly normalizedName: string;
  readonly shortDescription?: string;
  readonly description?: string;
  readonly type: WellbeingMaterialType;
  readonly externalUrl?: string;
  readonly bodyHtml?: string;
  readonly categoryUid?: string;
  readonly categoryName?: string;
  readonly isPublic: boolean;
  readonly isPublished: boolean;
  readonly filesCount: number;
  readonly kpiTagsCount: number;
}

export interface WellbeingMaterialData extends Pagination<WellbeingMaterial> {}

export interface WellbeingMaterialInput {
  readonly uid?: string;
  readonly rowVersion?: string;
  readonly name?: string;
  readonly shortDescription?: string;
  readonly description?: string;
  readonly type?: WellbeingMaterialType;
  readonly externalUrl?: string;
  readonly bodyHtml?: string;
  readonly categoryUid?: string;
  readonly isPublic?: boolean;
  readonly isPublished?: boolean;
}

export interface WellbeingMaterialCategory {
  readonly uid: string;
  readonly createdOn: string;
  readonly createdBy: string;
  readonly createdByUserUserName: string;
  readonly updatedOn: string;
  readonly updatedBy: string;
  readonly updatedByUserUserName: string;
  readonly isDeleted: boolean;
  readonly rowVersion: string;
  readonly name: string;
  readonly normalizedName: string;
  readonly description?: string;
  readonly materialsCount: number;
}

export interface WellbeingMaterialCategoryData
  extends Pagination<WellbeingMaterialCategory> {}

export interface WellbeingMaterialCategoryInput {
  readonly uid?: string;
  readonly rowVersion?: string;
  readonly name?: string;
  readonly description?: string;
}

// A material's attached file (Document materials).
export interface WellbeingMaterialFile {
  readonly uid: string;
  readonly createdOn: string;
  readonly rowVersion: string;
  readonly fileName: string;
  readonly fileExtension: string;
  readonly materialUid: string;
  readonly materialName: string;
}

export interface WellbeingMaterialFileData
  extends Pagination<WellbeingMaterialFile> {}

// A KPI tag on a material (band optional → "Any result").
export interface WellbeingMaterialKpi {
  readonly uid: string;
  readonly materialUid: string;
  readonly materialName: string;
  readonly kpiDefinitionUid: string;
  readonly kpiDefinitionName: string;
  readonly resultingRangeUid?: string;
  readonly resultingRangeLabel?: string;
}

export interface WellbeingMaterialKpiData
  extends Pagination<WellbeingMaterialKpi> {}

// Single tag to POST (band-agnostic when resultingRangeUid is null).
export interface WellbeingMaterialKpiInput {
  readonly kpiDefinitionUid: string;
  readonly resultingRangeUid?: string;
}

// One type covering company/department/user assignment response rows. The target uid/name field
// varies by endpoint (companyUid/companyName, departmentUid/departmentName, userUid/userUserName)
// so all are optional here.
export interface WellbeingMaterialAssignment {
  readonly uid: string;
  readonly createdOn: string;
  readonly rowVersion: string;
  readonly materialUid: string;
  readonly materialName: string;
  readonly companyUid?: string;
  readonly companyName?: string;
  readonly departmentUid?: string;
  readonly departmentName?: string;
  readonly userUid?: string;
  readonly userUserName?: string;
  readonly assignedByUserUid?: string;
  readonly assignedByUserUserName?: string;
  readonly assignmentDate: string;
  readonly isRequired: boolean;
}

export interface WellbeingMaterialAssignmentData
  extends Pagination<WellbeingMaterialAssignment> {}

// Per-user engagement lifecycle for a material (string-serialized enum from the backend). NotStarted is computed
// (assigned-but-untouched) — a stored engagement record only ever holds InProgress or Completed.
export enum MaterialEngagementStatus {
  NotStarted = "NotStarted",
  InProgress = "InProgress",
  Completed = "Completed",
}

export interface WellbeingMaterialEngagement {
  readonly uid: string;
  readonly createdOn: string;
  readonly rowVersion: string;
  readonly materialUid: string;
  readonly materialName: string;
  readonly userUid: string;
  readonly userUserName: string;
  readonly companyUid?: string;
  readonly companyName?: string;
  readonly status: MaterialEngagementStatus;
  readonly startedAt?: string;
  readonly completedAt?: string;
}

export interface WellbeingMaterialEngagementData
  extends Pagination<WellbeingMaterialEngagement> {}

// Coverage roster — a material's assigned population + each user's lifecycle status (NotStarted for an
// assigned-but-untouched user) + a summary over the whole population.
export interface WellbeingMaterialCoverageRow {
  readonly userUid: string;
  readonly userUserName?: string;
  readonly companyUid?: string;
  readonly companyName?: string;
  readonly departmentUid?: string;
  readonly departmentName?: string;
  readonly isRequired: boolean;
  readonly status: MaterialEngagementStatus;
  readonly startedAt?: string;
  readonly completedAt?: string;
}

export interface WellbeingMaterialCoverageSummary {
  readonly total: number;
  readonly notStarted: number;
  readonly inProgress: number;
  readonly completed: number;
  readonly completionRate: number;
}

export interface WellbeingMaterialCoverage {
  readonly data: WellbeingMaterialCoverageRow[];
  readonly pageNumber: number;
  readonly pageSize: number;
  readonly totalRecords: number;
  readonly summary: WellbeingMaterialCoverageSummary;
}

// A recommended material plus the human-readable reason it surfaced (Source) and — for the self-scoped
// consumption read — the caller's own engagement status (additive; absent/NotStarted in the admin preview).
export interface WellbeingMaterialRecommendation extends WellbeingMaterial {
  readonly source: string;
  readonly status?: MaterialEngagementStatus;
}

export interface WellbeingMaterialRecommendationData
  extends Pagination<WellbeingMaterialRecommendation> {}

// ---- Dashboard aggregates ----

// Time bucket for the dashboard trends — the server aggregates per day, week or month so long
// periods stay readable.
export type DashboardBucket = "day" | "week" | "month";

// Filters shared by the dashboard aggregate endpoints. Every filter is optional — omitting them
// widens the rollup to platform-wide / all-time. `from`/`to` are ISO datetime bounds.
export interface DashboardFilters {
  readonly companyUid?: string;
  readonly departmentUid?: string;
  readonly questionnaireUid?: string;
  readonly from?: string;
  readonly to?: string;
  readonly bucket?: DashboardBucket;
}

// One time bucket of login activity (date = the bucket's start date): every login vs the
// distinct users behind them.
export interface DashboardLoginsByDay {
  readonly date: string;
  readonly total: number;
  readonly unique: number;
}

// How often the period's active users signed in — band is a sign-in count range (e.g. "1",
// "2–5", "6+"); user counts sum to the period's unique users.
export interface DashboardLoginFrequencyBand {
  readonly band: string;
  readonly users: number;
}

// Login concentration per company (platform-wide) or per department (when a company filter is
// set), ranked by total logins.
export interface DashboardLoginsByGroup {
  readonly label: string;
  readonly total: number;
  readonly unique: number;
}

// User registration + login rollup for the selected scope and period.
export interface DashboardUserActivity {
  readonly registeredUsers: number;
  readonly registeredInPeriod: number;
  readonly totalLogins: number;
  readonly uniqueLogins: number;
  /** The time bucket the login trend was aggregated with (echoes the request). */
  readonly bucket: DashboardBucket;
  readonly loginsByDay: DashboardLoginsByDay[];
  readonly loginFrequency: DashboardLoginFrequencyBand[];
  /** The dimension loginsByGroup is grouped by — department when a company filter is set. */
  readonly groupBy: "company" | "department";
  readonly loginsByGroup: DashboardLoginsByGroup[];
}

// One time bucket of assessment activity (date = the bucket's start date).
export interface DashboardQuestionnaireSeriesPoint {
  readonly date: string;
  readonly total: number;
  readonly completed: number;
}

// Per-questionnaire activity, ranked by submission volume.
export interface DashboardQuestionnaireBreakdown {
  readonly label: string;
  readonly total: number;
  readonly completed: number;
}

// Per-company assessment coverage: the share of registered users who have submitted anything
// (rate 0..100), lowest first. Platform view only — empty when a company filter is set.
export interface DashboardCompanyCoverage {
  readonly label: string;
  readonly respondents: number;
  readonly registered: number;
  readonly rate: number;
}

// Questionnaire completion rollup: how many assessments exist in scope, how far along they are,
// who they reach, plus per-bucket, per-questionnaire and per-company breakdowns.
export interface DashboardQuestionnaireStatus {
  readonly total: number;
  readonly completed: number;
  readonly inProgress: number;
  readonly scoringCompleted: number;
  /** Distinct users behind the submissions in scope. */
  readonly respondents: number;
  readonly registeredInScope: number;
  /** The time bucket the series was aggregated with (echoes the request). */
  readonly bucket: DashboardBucket;
  readonly series: DashboardQuestionnaireSeriesPoint[];
  readonly byQuestionnaire: DashboardQuestionnaireBreakdown[];
  readonly coverageByCompany: DashboardCompanyCoverage[];
  /** How many companies coverageByCompany was ranked over (it carries only the lowest N). */
  readonly coverageTotalCompanies: number;
}

// ---- Incidents rollup ----

// One age×severity cell of the open-incident backlog (band = days open, e.g. "0–7").
export interface DashboardIncidentAgingBand {
  readonly band: string;
  readonly severity: string;
  readonly count: number;
}

// One severity×status cell over the incidents in scope.
export interface DashboardIncidentSeverityStatus {
  readonly severity: string;
  readonly status: string;
  readonly count: number;
}

// A company's open-incident load, worst-first.
export interface DashboardIncidentCompany {
  readonly companyUid: string;
  readonly companyName: string;
  readonly open: number;
  readonly highSeverityOpen: number;
  readonly unassignedOpen: number;
  readonly oldestOpenDays: number;
}

// One server page of the per-company open-incident ranking (worst-first, server-paged).
export type DashboardIncidentCompaniesPage = Pagination<DashboardIncidentCompany>;

// One time bucket of incident flow: incidents created vs incidents resolved (resolution is
// approximated by the incident's last update date).
export interface DashboardIncidentFlowPoint {
  readonly date: string;
  readonly created: number;
  readonly resolved: number;
}

// Incident triage rollup. Backlog figures (open/unassigned/high-severity/aging) are point-in-time
// snapshots and ignore the date range; the flow series honours it. The per-company ranking is
// served separately (server-paged) — see DashboardIncidentCompaniesPage.
export interface DashboardIncidents {
  readonly openIncidents: number;
  readonly unassignedOpen: number;
  readonly highSeverityOpen: number;
  readonly avgOpenAgeDays: number;
  readonly resolvedInPeriod: number;
  readonly bucket: DashboardBucket;
  readonly agingBands: DashboardIncidentAgingBand[];
  readonly severityStatus: DashboardIncidentSeverityStatus[];
  readonly flow: DashboardIncidentFlowPoint[];
}

// ---- Rewards & loyalty rollup ----

// One time bucket of the points economy: points earned vs points spent.
export interface DashboardEconomyPoint {
  readonly date: string;
  readonly earned: number;
  readonly spent: number;
}

// Age of the pending redemption queue (band = days waiting, e.g. "0–2").
export interface DashboardQueueAgingBand {
  readonly band: string;
  readonly count: number;
}

// A reward's redemption demand: request count + the points it moved.
export interface DashboardTopReward {
  readonly label: string;
  readonly count: number;
  readonly points: number;
}

// Redemption requests per workflow status.
export interface DashboardStatusCount {
  readonly status: string;
  readonly count: number;
}

// Loyalty participation per company (platform-wide) or department (when a company filter is
// set): the share of registered users who have earned points (rate 0..100).
export interface DashboardParticipationGroup {
  readonly label: string;
  readonly earners: number;
  readonly registered: number;
  readonly rate: number;
}

// Rewards & loyalty rollup: the redemption queue health, the points economy over time, and
// where the programme actually lands with users.
export interface DashboardRewardsLoyalty {
  readonly pendingRedemptions: number;
  /** All redemption requests in the period (cancelled excluded) — the whole topRewards is a share of. */
  readonly totalRedemptions: number;
  /** Approved share of decided redemptions, 0..100. */
  readonly approvalRate: number;
  readonly avgDecisionDays: number;
  readonly pointsEarned: number;
  readonly pointsSpent: number;
  readonly outstandingBalance: number;
  readonly bucket: DashboardBucket;
  readonly economy: DashboardEconomyPoint[];
  readonly queueAging: DashboardQueueAgingBand[];
  readonly topRewards: DashboardTopReward[];
  readonly statusBreakdown: DashboardStatusCount[];
  /** The dimension participation is grouped by — department when a company filter is set. */
  readonly groupBy: "company" | "department";
  readonly participation: DashboardParticipationGroup[];
  /** How many groups participation was ranked over (it carries only the top N). */
  readonly participationTotalGroups: number;
}

// One band of the scored-results distribution. Ordinal ascending = riskiest band first;
// bandColor is the band's own configured colour when one is set.
export interface DashboardRiskBand {
  readonly bandLabel: string;
  readonly bandColor: string | null;
  readonly ordinal: number;
  readonly count: number;
}

// A company's share of high-risk results (results landing in the lowest band of a KPI's scale).
// highRiskShare is a percentage (0..100).
export interface DashboardRiskCompany {
  readonly companyUid: string;
  readonly companyName: string;
  readonly totalResults: number;
  readonly highRiskCount: number;
  readonly highRiskShare: number;
}

// One server page of the companies-at-risk ranking (worst-first by high-risk share, server-paged).
export type DashboardRiskCompaniesPage = Pagination<DashboardRiskCompany>;

// Risk overview rollup: the band distribution of every scored KPI result in scope. The
// worst-first company ranking is served separately (server-paged) — see DashboardRiskCompaniesPage.
export interface DashboardRisks {
  readonly totalResults: number;
  readonly respondentCount: number;
  /** Completed answers not yet scored — their results aren't in the distribution yet. */
  readonly awaitingScoring: number;
  readonly bandDistribution: DashboardRiskBand[];
}

// One recurring risk domain — the questionnaire SECTION a KPI's questions belong to.
export interface DashboardRiskDomain {
  readonly domain: string;
  readonly totalResults: number;
  readonly highRiskCount: number;
  /** High-risk results as a percentage of this domain's results. */
  readonly highRiskShare: number;
}

export interface DashboardRiskDomains {
  readonly totalResults: number;
  /** Results whose KPI spans several sections, so no single domain can be credited. */
  readonly unattributedResults: number;
  readonly domains: DashboardRiskDomain[];
}

// One bucket of the risk trend: how many results, and how many of them were high-risk.
export interface DashboardRiskTrendPoint {
  /** Bucket start, yyyy-MM-dd. */
  readonly date: string;
  readonly totalResults: number;
  readonly highRiskCount: number;
  readonly highRiskShare: number;
}

export interface DashboardRiskTrend {
  /** day | week | month. */
  readonly bucket: string;
  readonly series: DashboardRiskTrendPoint[];
}

// ---- Affiliates rollup ----

// One age×status cell of the open counselling-request backlog (band = days waiting, e.g. "0–3";
// status = Requested or Proposed).
export interface DashboardAffiliateAgeBand {
  readonly band: string;
  readonly status: string;
  readonly count: number;
}

// One time bucket of counselling flow: requests raised vs sessions held.
export interface DashboardAffiliateDemandPoint {
  readonly date: string;
  readonly raised: number;
  readonly held: number;
}

// One band of the report review turnaround (band = submit→appraise days, e.g. "0–2"; the server
// always delivers all four bands in pinned order).
export interface DashboardAffiliateTurnaroundBand {
  readonly band: string;
  readonly count: number;
}

// One time bucket of training flow: trainings planned (created) vs held (completed).
export interface DashboardAffiliateTrainingPoint {
  readonly date: string;
  readonly planned: number;
  readonly held: number;
}

// Affiliates rollup: the counselling queue health, the demand-vs-delivery flow, report appraisal
// turnaround and the trainings pipeline. Backlog figures (open/unassigned/age) are point-in-time
// snapshots and ignore the date range; the trends and turnaround honour it. The per-affiliate
// workload roster is served separately (server-paged) — see DashboardAffiliateWorkloadPage.
export interface DashboardAffiliates {
  readonly openRequests: number;
  readonly unassignedOpenRequests: number;
  readonly avgOpenAgeDays: number;
  /** Completed share of concluded counselling requests, 0..100. */
  readonly completionRate: number;
  readonly reportsAwaitingAppraisal: number;
  readonly upcomingTrainings: number;
  readonly trainingsHeld: number;
  /** Trainings cancelled within the period (approximated by the cancellation being the last update). */
  readonly trainingsCancelled: number;
  /** Share of marked attendance across held trainings, 0..100 — null when none was marked. */
  readonly attendanceRate: number | null;
  readonly bucket: DashboardBucket;
  readonly ageBands: DashboardAffiliateAgeBand[];
  readonly demandVsDelivery: DashboardAffiliateDemandPoint[];
  readonly reviewTurnaround: DashboardAffiliateTurnaroundBand[];
  readonly trainingsTrend: DashboardAffiliateTrainingPoint[];
}

// An affiliate's workload, worst-first (open requests, then planned trainings). The roster covers
// every affiliate — idle rows are the capacity headroom.
export interface DashboardAffiliateWorkload {
  readonly userUid: string;
  readonly userName: string;
  readonly openRequests: number;
  readonly sessionsHeld: number;
  readonly trainingsPlanned: number;
  readonly trainingsHeld: number;
  /** Share of marked attendance across the affiliate's held trainings — null when none was marked. */
  readonly attendanceRate: number | null;
}

// One server page of the per-affiliate workload roster (worst-first, server-paged).
export type DashboardAffiliateWorkloadPage = Pagination<DashboardAffiliateWorkload>;

// Targeting preview — resolves a staged set of assignment/invitation targets into the distinct
// user reach, before anything is submitted.

export type TargetingPreviewTargetType = "company" | "department" | "user";

export interface TargetingPreviewInput {
  readonly companyUids: string[];
  readonly departmentUids: string[];
  readonly userUids: string[];
  /** When true, a company target also counts its sub-companies' users (materials cascade; rewards/badges don't). */
  readonly includeSubCompanies?: boolean;
}

// One resolved target. `allowed=false` = the caller may not target this item (submitting would
// 403) — its `users` is 0 and it is excluded from `totalUsers`.
export interface TargetingPreviewTarget {
  readonly type: TargetingPreviewTargetType;
  readonly uid: string;
  readonly allowed: boolean;
  readonly users: number;
}

// The resolved reach: distinct users across all allowed targets (overlaps counted once).
export interface TargetingPreviewResult {
  readonly totalUsers: number;
  readonly targets: TargetingPreviewTarget[];
}
